import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { db, storage, ensureAuth, auth } from '../utils/firebase';
import { useAuth } from '../utils/AuthContext';
import { collection, query, where, getDocs, doc, updateDoc, Timestamp, addDoc, getDoc, onSnapshot, type QueryDocumentSnapshot } from 'firebase/firestore';
import { getProposalBuilderBySlug } from '../utils/portalPinService';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getPublicSettings, AppSettings } from '../utils/appSettings';
import { ProposalBuilderData, ProposalBonding, ProposalNodeSet, ProposalPhase, ProposalDownload, EnhancedDeal, ChangeOrder, ChangeOrderAuditEntry, PortalInstallmentPayment, PortalStatementEntry } from '../types';
import { bondingClause, bondingLineDescription, bondingLineLabel } from '../utils/proposalBonding';
import { ArrowLeft, ChevronDown, ChevronRight, Download, FileCheck, Loader2 } from 'lucide-react';
import SignaturePad from '../components/engagement/SignaturePad';
import { pdf } from '@react-pdf/renderer';
import ProposalPdfDocument, { proposalPdfDownloadName } from '../components/proposals/ProposalPdfDocument';
import { applyProfitMargin } from '../utils/proposalCalculations';
import { speciesTimberKitBillable } from '../utils/proposalSpeciesProduction';
import {
    computeProposalGrandTotals,
    calculateProposalNodeBuckets,
    calculateProposalNodeBucketsWithSelections,
    getEffectiveIncludeInstallForContractDisplay,
    getAppliedSignedStructureSnapshot,
    getClientVisibleNodeSets,
    buildDefaultStructureSelections,
    parseStructureSelectionsForPublicTotals,
    countClientSelectedStructures,
    getSignedGrandTotalsDisplay,
    clientStructureTimberKitForDisplay,
    needsDynamicClientPricing,
    filterToSignedProposalLayout,
    signedIncludedStructureLayoutKey,
    signedPdfCacheMatchesContractLayout,
} from '../utils/proposalPublicGrandTotals';
import {
    listExclusiveStructures,
    normalizeExclusiveGroupSelections,
    selectExclusiveGroupMember,
    structureIsExclusive,
} from '../utils/proposalExclusiveGroups';
import { projectCostPerSqFt, PROJECT_COST_PER_SQFT_HOVER } from '../utils/projectCostPerSqFt';
import { pickWonPipelineStageId } from '../utils/pipelineStageUtils';
import { resolveEnhancedDealIdForProposalDoc } from '../utils/proposalCanonicalDealId';
import { syncSignedDealSideEffects } from '../utils/signedDealSync';
import { isAlreadyWonDeal, shouldSetFirstWonAt } from '../utils/dealWonWriteOnce';
import { logPortalActivity, setPortalActivityStakeholderDisplayName, setPortalActivityStakeholderLeadId, setPortalActivityStakeholderLinkId, useProposalEngagementSession, logProposalDownloadToPortal } from '../utils/portalActivityLogger';
import { getPortalAccessMeta } from '../utils/portalPinService';
import {
    evaluateProposalSigningGate,
    isConceptApprovalFlowEnabled,
    mergePortalReleasesIntoSigningGateInput,
    portalConceptModelReturnPath,
    shouldRedirectProposalSignToConceptModel,
    shouldSkipConceptApprovalForSigning,
    type ProposalSigningGateInput,
} from '../utils/portalDeliverableApprovalService';
import { parsePortalProjectRow } from '../utils/portalProjectView';
import { isPortalStaffUser } from '../utils/userRoles';
import { isSampleGcPortalSlug, isSampleGcProposalSlug } from '../utils/sampleGcPortal';
import {
    verifyStakeholderTokenCallable,
    clearStakeholderSessionCallable,
    readStakeholderClaimsFromToken,
} from '../utils/stakeholderAuth';
import { summarizeTimbersForClient, type ClientTimberRow } from '../utils/clientTimberSummary';
import {
    structureHasIncludedDecking,
    structureDeckingIncludedLabel,
} from '../utils/proposalStructureDecking';
import {
    formatClientTimberSpecLabel,
    timberListForSpeciesSelection,
} from '../utils/proposalTimberDefaults';
import TimberSizesSheet from '../components/proposals/TimberSizesSheet';
import {
    buildPhaseStructureSelections,
    commitOfferedPhases,
    computePhaseCommitAmount,
    getNodeSetPhaseId,
    getOrderedPhases,
    getProposalPhaseById,
    groupNodeSetsByPhase,
    isPhasedProposal,
} from '../utils/proposalPhases';
import { removeUndefinedDeep } from '../utils/removeUndefinedDeep';
import { subscribeChangeOrdersForDeal, getChangeOrderReference } from '../utils/changeOrders';
import { syncApprovedChangeOrder } from '../utils/changeOrderSync';
import { computePriorContractValueBeforeChangeOrder } from '../utils/contractValue';
import { buildPaymentScheduleBreakdown, invoicedInstallmentLocks } from '../utils/paymentSchedule';
import PaymentScheduleBreakdown from '../components/proposals/PaymentScheduleBreakdown';
import PortalStatementButton from '../components/portal/PortalStatementButton';
import {
    CLIENT_DIRECTION_SIGNATURE_LABEL,
    clientDirectionSignatureDataUrl,
    hasClientPortalSignature,
} from '../utils/internalProposalSign';
import ChangeOrderPdfDocument from '../components/proposals/ChangeOrderPdfDocument';
import './PublicProposalPage.css';

function StructureDeckingNote({ materials }: { materials: ProposalNodeSet['materials'] }) {
    if (!structureHasIncludedDecking(materials)) return null;
    return (
        <span className="structure-decking-included">
            {structureDeckingIncludedLabel(materials)}
        </span>
    );
}

function downloadBlobAsFile(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

async function downloadPdfFromStorageUrl(url: string, filename: string): Promise<boolean> {
    try {
        const response = await fetch(url);
        if (!response.ok) return false;
        const blob = await response.blob();
        downloadBlobAsFile(blob, filename);
        return true;
    } catch {
        return false;
    }
}

function parseProposalSignatureDate(signedAt: unknown, fallback?: unknown): Date {
    if (signedAt instanceof Date) return signedAt;
    if (signedAt && typeof (signedAt as { toDate?: () => Date }).toDate === 'function') {
        return (signedAt as { toDate: () => Date }).toDate();
    }
    if (signedAt && typeof (signedAt as { seconds?: number }).seconds === 'number') {
        return new Date((signedAt as { seconds: number }).seconds * 1000);
    }
    if (fallback instanceof Date) return fallback;
    if (fallback && typeof (fallback as { toDate?: () => Date }).toDate === 'function') {
        return (fallback as { toDate: () => Date }).toDate();
    }
    if (fallback && typeof (fallback as { seconds?: number }).seconds === 'number') {
        return new Date((fallback as { seconds: number }).seconds * 1000);
    }
    return new Date();
}

function resolveSignedProposalPdfSignature(
    proposal: ProposalBuilderData,
): { dataUrl: string; signedBy: string; signedAt: Date } | null {
    const sig = proposal.signature as
        | { dataUrl?: string; signedBy?: string; signedAt?: unknown; source?: string }
        | undefined;

    if (sig?.dataUrl) {
        return {
            dataUrl: sig.dataUrl,
            signedBy:
                sig.signedBy ||
                (hasClientPortalSignature(sig) ? 'Customer' : CLIENT_DIRECTION_SIGNATURE_LABEL),
            signedAt: parseProposalSignatureDate(sig.signedAt, proposal.approvedAt),
        };
    }

    if (proposal.status === 'approved') {
        const dataUrl = clientDirectionSignatureDataUrl();
        if (!dataUrl) return null;
        return {
            dataUrl,
            signedBy: CLIENT_DIRECTION_SIGNATURE_LABEL,
            signedAt: parseProposalSignatureDate(proposal.approvedAt, proposal.approvedAt),
        };
    }

    return null;
}

async function loadPdfLogoBase64(logo?: { url?: string } | null): Promise<string | undefined> {
    if (logo?.url) {
        try {
            const logoUrl = logo.url.includes('?')
                ? `${logo.url}&t=${Date.now()}`
                : `${logo.url}?t=${Date.now()}`;
            const response = await fetch(logoUrl, {
                mode: 'cors',
                cache: 'no-store',
                credentials: 'omit',
            });
            if (response.ok) {
                const blob = await response.blob();
                return await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result as string);
                    reader.onerror = () => reject(new Error('FileReader failed'));
                    reader.readAsDataURL(blob);
                });
            }
        } catch {
            // fall through to local asset
        }
    }

    try {
        const fallbackResponse = await fetch('/assets/logo/LC_pdf_logo.png');
        if (fallbackResponse.ok) {
            const fallbackBlob = await fallbackResponse.blob();
            return await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.onerror = () => reject(new Error('FileReader failed'));
                reader.readAsDataURL(fallbackBlob);
            });
        }
    } catch {
        // logo optional
    }

    return undefined;
}

// Collapsible Section Component
interface CollapsibleSectionProps {
    title: string;
    children: React.ReactNode;
    defaultOpen?: boolean;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
    title,
    children,
    defaultOpen = false,
}) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <section className={`collapsible-section ${isOpen ? 'open' : ''}`}>
            <div className="collapsible-header" onClick={() => setIsOpen(!isOpen)}>
                {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                <h3 className="collapsible-title">{title}</h3>
            </div>
            {isOpen && <div className="collapsible-content">{children}</div>}
        </section>
    );
};

// Terms and Conditions content - returns dynamic content with client name.
// `bonding` adds a BONDING paragraph under PAYMENTS only when the proposal carries a premium.
const getProposalTermsConditions = (clientName: string, bonding?: ProposalBonding | null) => `1. OVERVIEW.
This Construction Contract is being entered into between ${clientName || "[Client Name]"} ("Owner") and MJL Enterprises, LLC dba Leiper's Creek Timber Frames ("Contractor"), TN Contractor License #78475, (collectively, the "Parties").

The effective date of this Construction Contract will be the date of the last signature.

This Construction Contract, along with incorporated documents referenced herein (if any), sets forth the terms and conditions agreed to between the Parties relating to the Work by Contractor for Owner.

GENERAL LIMITATION OF LIABILITY
Unless otherwise more narrowly limited or required by law, and notwithstanding any other provision in this Contract, the total liability of Leiper's Creek Timber Frames pertaining to any Work or other activity under this Construction Contract, including without limitation, its employees, managers and members ("Contractor") to Owner, Owner's affiliates, insurers, licensees, invitees and any third parties referred to below (collectively referred to as "Owner") shall be limited to re-performing, at Contractor's expense, that portion of the work and services for which such breach or otherwise has occurred and provided that any such claim or demand shall be made in writing to Contractor prior to the terminating of this Contract.

GENERAL TERMS
This Construction Contract may not be assigned by either Party without written consent of the other Party and such consent is not to be unreasonably withheld. If any provision of this Construction Contract is found to be invalid, illegal, or unenforceable, the remaining portions shall remain in full force and effect. This Construction Contract is governed by and is to be interpreted under the laws of the State of Tennessee and any legal proceedings relating to this Construction Contract will be maintained only in Williamson County in the State of Tennessee.

2. OWNER'S RESPONSIBILITY.
The Owner will be responsible for properly marking the location of any privatized utilities, site lighting, low voltage lighting, invisible pet fences, underground drainage pipes, sprinkler and irrigation systems, and property lines that may be impacted by the Work if Leiper's Creek Timber Frames is the contracted installer of the Work. Contractor shall not be responsible for any damage occurring by reason of owner's failure to locate and mark all underground obstructions or by reason of the Work area encroaching upon property lines.

3. CONTRACTOR'S RESPONSIBILITY.
Contractor will perform the Work in a reasonably workmanlike manner and will undertake to complete the same within a reasonable period of time. Contractor will maintain, throughout the duration of this Construction Contract, all legally required licensure and permissions to perform the Work required for the Construction Project. The Contractor will purchase and maintain general liability insurance during the course of Work on the Property with duly licensed insurance companies in the amount of no less than $1 million dollars.

4. SCOPE OF WORK.
The work that Contractor (and/or Contractor's agents) are obligated to perform for Owner is as set forth in "Your Investment" above.

CHANGE ORDERS
From time to time during the performance of Work under this Construction Contract, there may be changes required or requested to the scope of work, price, and time for completion. All such proposed changes will be submitted to the other Party in writing containing at least the following information: proposal date, the change requested, explanation of how that will affect the cost or time of completion, and signed by the proposing party. When both parties have signed to acknowledge their approval of such a proposal, it will be designated as a "Change Order" and will then be incorporated into this Construction Contract.

5. PAYMENTS.
Owner shall make the payments to Contractor for the Work as specified in "Your Investment". One and one-half (1.5%) percent interest will be added to the balance due each month as a late charge on any past due amount. Upon default of payment due, all costs of collection, court costs and reasonable attorney fees for collection of the Agreement will be added to Agreement price (final invoice amount). This agreement is made in Thompsons Station, Williamson County, Tennessee and deemed signed in Williamson County, Tennessee and all payments are due at Leiper's Creek Timber Frames, 3745 Robinson Rd., Thompsons Station, TN 37179.
${bondingClause(bonding) ? `
BONDING
${bondingClause(bonding)}
` : ''}
6. DISCLAIMER.
OWNER ACKNOWLEDGES THAT, EXCEPT AS SET FORTH HEREIN, ALL SERVICES ARE PROVIDED WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, FITNESS FOR A PARTICULAR PURPOSE. CONTRACTOR DOES NOT AUTHORIZE ANYONE TO MAKE A WARRANTY OF ANY KIND ON CONTRACTOR'S BEHALF AND OWNER SHOULD NOT RELY ON ANY SUCH STATEMENTS.

7. ADDITIONAL PROVISIONS.
This section is blank.`;

const PublicProposalPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();
    const portalSlug = searchParams.get('portalSlug');
    const cameFromPortal = searchParams.get('from') === 'portal' && !!portalSlug;
    const portalProjectDealId = searchParams.get('project');
    const displayProjectNameFromPortal = searchParams.get('displayProjectName');
    const stakeholderTokenParam = searchParams.get('stakeholderToken');
    const [stakeholderDisplayName, setStakeholderDisplayName] = useState<string | null>(null);
    const [stakeholderCompanyName, setStakeholderCompanyName] = useState<string | null>(null);
    const [stakeholderLeadId, setStakeholderLeadId] = useState<string | null>(null);
    const [stakeholderLinkId, setStakeholderLinkId] = useState<string | null>(null);
    const [proposal, setProposal] = useState<ProposalBuilderData | null>(null);
    const [proposalId, setProposalId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Determine if viewer is authenticated internal staff or stakeholder (view-only)
    const isStaffPortalUser = !!user && isPortalStaffUser(user.role);
    const isStakeholderViewer = !isStaffPortalUser && stakeholderDisplayName !== null;
    const isSampleGcDemo =
        isSampleGcPortalSlug(portalSlug) || isSampleGcProposalSlug(slug) || isSampleGcProposalSlug(proposal?.slug);
    const viewerActorType = isStaffPortalUser ? 'employee' : isStakeholderViewer ? 'stakeholder' : 'client';
    const viewerActorName = isStaffPortalUser
        ? (user.name || user.email || 'Staff')
        : isStakeholderViewer
            ? stakeholderDisplayName
            : null; // null = use clientName fallback

    useEffect(() => {
        setPortalActivityStakeholderDisplayName(stakeholderDisplayName);
        return () => setPortalActivityStakeholderDisplayName(null);
    }, [stakeholderDisplayName]);

    useEffect(() => {
        setPortalActivityStakeholderLeadId(stakeholderLeadId);
        return () => setPortalActivityStakeholderLeadId(null);
    }, [stakeholderLeadId]);

    useEffect(() => {
        setPortalActivityStakeholderLinkId(stakeholderLinkId);
        return () => setPortalActivityStakeholderLinkId(null);
    }, [stakeholderLinkId]);

    // Settings state
    const [companyLogo, setCompanyLogo] = useState<{ url: string; name: string } | null>(null);
    const [companyBackground, setCompanyBackground] = useState<{ url: string; name: string } | null>(null);
    const [videoUrl, setVideoUrl] = useState<string | null>(null);

    // Access gate state (no longer uses PIN entry — must come from portal)
    const [accessDenied, setAccessDenied] = useState(false);

    // Expanded buckets state
    const [expandedBuckets, setExpandedBuckets] = useState<{ [key: string]: boolean }>({});

    // Currently-open structure id for the timber-sizes sheet, or null when
    // the sheet is closed. Gated upstream by `proposal.publicSettings.showTimberSizes`.
    const [openTimberFor, setOpenTimberFor] = useState<string | null>(null);

    /**
     * Privacy-safe timber summary per structure. Computed once per proposal
     * mutation so opening the sheet is instant and the work doesn't repeat
     * on every render.
     */
    const timberSummariesByStructureId = useMemo<Record<string, ClientTimberRow[]>>(() => {
        const out: Record<string, ClientTimberRow[]> = {};
        if (!proposal?.nodeSets) return out;
        const layoutSets = filterToSignedProposalLayout(
            proposal,
            proposal.nodeSets.filter(ns => ns.enabled !== false),
        );
        for (const ns of layoutSets) {
            const list = ns.materials?.timberList || [];
            const summary = summarizeTimbersForClient(list);
            if (summary.length > 0) out[ns.id] = summary;
        }
        return out;
    }, [proposal?.nodeSets]);

    const showTimberSizes = !!proposal?.publicSettings?.showTimberSizes;

    // Multi-species state - per structure selection (structureId -> speciesName)
    const [selectedSpecies, setSelectedSpecies] = useState<Record<string, string>>({});

    // Kit proposal species selection (single global species for kit proposals)
    const [kitSelectedSpecies, setKitSelectedSpecies] = useState<string | null>(null);

    // Client install selection (when installOptional is true)
    const [includeInstall, setIncludeInstall] = useState(true);

    // Client structure selection (when clientOptional is true on a structure)
    const [structureSelections, setStructureSelections] = useState<Record<string, boolean>>({});
    // Phased projects: deal change orders (phase commitments) + later-phase signing state
    const [dealChangeOrders, setDealChangeOrders] = useState<ChangeOrder[]>([]);
    // Per-installment paid status mirrored onto the portal project (deposit/pre_shipment/final).
    const [installmentMirror, setInstallmentMirror] = useState<PortalInstallmentPayment[] | undefined>(undefined);
    const [portalStatement, setPortalStatement] = useState<PortalStatementEntry[]>([]);
    const [phaseSigningId, setPhaseSigningId] = useState<string | null>(null);
    const [phaseSignError, setPhaseSignError] = useState<string | null>(null);

    // PDF download state
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [generatingSignedPdf, setGeneratingSignedPdf] = useState(false);

    // Lightbox state
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxImages, setLightboxImages] = useState<Array<{ url: string; name?: string }>>([]);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    // Ref to prevent duplicate activity logging (React StrictMode runs effects twice)
    const viewLoggedRef = useRef(false);

    /** When opening from portal without displayProjectName in URL (legacy links), load tab title from Firestore. */
    const [portalTabProjectNameFallback, setPortalTabProjectNameFallback] = useState<string | null>(null);
    /** Resolved Firestore portal doc id — used for engagement duration tracking. */
    const [resolvedPortalId, setResolvedPortalId] = useState<string | null>(null);
    const [portalGateContext, setPortalGateContext] = useState<{
        loading: boolean;
        gateInput: ProposalSigningGateInput | null;
        portalSlugForReturn?: string;
    }>({ loading: true, gateInput: null });
    /** Canonical naming from the linked CRM deal (project number, descriptor, contact name). */
    const [linkedDeal, setLinkedDeal] = useState<Pick<
        EnhancedDeal,
        'projectNumber' | 'clientLabel' | 'descriptor' | 'title' | 'contactName' | 'company' | 'leadId'
    > | null>(null);
    /** Live portal profile (staff edits client name/company on the workspace). */
    const [portalClientIdentity, setPortalClientIdentity] = useState<{
        name?: string;
        company?: string;
    } | null>(null);
    /** Live lead record linked from the CRM deal (staff lead edits). */
    const [linkedLeadIdentity, setLinkedLeadIdentity] = useState<{
        name?: string;
        company?: string;
    } | null>(null);

    useEffect(() => {
        if (!cameFromPortal || !portalSlug || !portalProjectDealId || displayProjectNameFromPortal) {
            setPortalTabProjectNameFallback(null);
            return;
        }
        let cancelled = false;
        void (async () => {
            try {
                // Load by portalId — slug list queries denied under PIN-scoped rules.
                const accessMeta = await getPortalAccessMeta(portalSlug);
                const snap = await getDoc(doc(db, 'clientPortals', accessMeta.portalId));
                if (cancelled || !snap.exists()) return;
                const pd = snap.data() as { projects?: Record<string, { projectName?: string }> };
                const pn = pd.projects?.[portalProjectDealId]?.projectName?.trim();
                if (pn && !cancelled) setPortalTabProjectNameFallback(pn);
            } catch {
                if (!cancelled) setPortalTabProjectNameFallback(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [cameFromPortal, portalSlug, portalProjectDealId, displayProjectNameFromPortal]);

    useEffect(() => {
        if (portalSlug === 'preview') {
            setResolvedPortalId(null);
            return;
        }
        let cancelled = false;
        void (async () => {
            try {
                let portalDocId: string | null = null;

                // Guests cannot list clientPortals by dealId/proposalSlug under PIN rules —
                // resolve via server access meta when returning from a portal, else leave null.
                if (cameFromPortal && portalSlug) {
                    try {
                        const accessMeta = await getPortalAccessMeta(portalSlug);
                        portalDocId = accessMeta.portalId;
                    } catch (err) {
                        console.error(
                            '[Proposal] getPortalAccessMeta failed while resolving portalId:',
                            err,
                        );
                    }
                } else if (proposal?.dealId || proposal?.slug) {
                    console.warn(
                        '[Proposal] Skipping clientPortals dealId/proposalSlug list lookup (denied for guests); portalId unresolved.',
                    );
                }

                if (!cancelled) setResolvedPortalId(portalDocId);
            } catch (err) {
                console.error('[Proposal] Failed to resolve portalId:', err);
                if (!cancelled) setResolvedPortalId(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [cameFromPortal, portalSlug, proposal?.dealId, proposal?.slug]);

    useEffect(() => {
        if (!resolvedPortalId) {
            setPortalClientIdentity(null);
            return;
        }
        return onSnapshot(doc(db, 'clientPortals', resolvedPortalId), (snap) => {
            if (!snap.exists()) {
                setPortalClientIdentity(null);
                return;
            }
            const data = snap.data() as Record<string, unknown>;
            const name = typeof data.clientName === 'string' ? data.clientName.trim() : '';
            const company = typeof data.clientCompany === 'string' ? data.clientCompany.trim() : '';
            setPortalClientIdentity({
                name: name || undefined,
                company: company || undefined,
            });
        });
    }, [resolvedPortalId]);

    useEffect(() => {
        if (!proposal || isStakeholderViewer) {
            setPortalGateContext({
                loading: false,
                gateInput: null,
            });
            return;
        }
        if (authLoading) {
            setPortalGateContext(prev => ({ ...prev, loading: true }));
            return;
        }

        const dealId = portalProjectDealId || proposal.dealId || '';
        let cancelled = false;

        void (async () => {
            setPortalGateContext(prev => ({ ...prev, loading: true }));
            try {
                await ensureAuth();
                let portalSnap: QueryDocumentSnapshot | null = null;

                if (cameFromPortal && portalSlug) {
                    try {
                        const accessMeta = await getPortalAccessMeta(portalSlug);
                        const byId = await getDoc(doc(db, 'clientPortals', accessMeta.portalId));
                        if (byId.exists()) portalSnap = byId as QueryDocumentSnapshot;
                    } catch (err) {
                        console.error(
                            '[Proposal] Portal getDoc for signing gate failed:',
                            err,
                        );
                    }
                } else if (dealId || proposal.slug) {
                    console.warn(
                        '[Proposal] Skipping clientPortals dealId/proposalSlug list lookup for gate (denied for guests).',
                    );
                }

                if (cancelled || !portalSnap) {
                    setPortalGateContext({
                        loading: false,
                        gateInput: null,
                    });
                    return;
                }

                const pd = portalSnap.data() as Record<string, unknown>;
                const projects = pd.projects as Record<string, Record<string, unknown>> | undefined;
                const projectRaw = projects?.[dealId];
                const project = parsePortalProjectRow(
                    dealId,
                    (projectRaw || pd) as Record<string, unknown>,
                );

                const statementSource = (projectRaw && Array.isArray(projectRaw.statement))
                    ? projectRaw.statement
                    : pd.statement;
                if (!cancelled) {
                    setInstallmentMirror(project.installmentPayments);
                    setPortalStatement(Array.isArray(statementSource) ? statementSource as PortalStatementEntry[] : []);
                }

                let releaseRows: Array<{
                    id?: string;
                    releasedUrl?: string;
                    streamKind?: string;
                    portalTarget?: string;
                    releaseVersionIndex?: number;
                }> = [];
                if (dealId) {
                    try {
                        const relSnap = await getDocs(
                            query(
                                collection(db, 'clientPortals', portalSnap.id, 'portalFileReleases'),
                                where('dealId', '==', dealId),
                            ),
                        );
                        releaseRows = relSnap.docs.map(d => {
                            const v = d.data();
                            return {
                                id: d.id,
                                releasedUrl: typeof v.releasedUrl === 'string' ? v.releasedUrl : undefined,
                                streamKind: typeof v.streamKind === 'string' ? v.streamKind : undefined,
                                portalTarget: typeof v.portalTarget === 'string' ? v.portalTarget : undefined,
                                releaseVersionIndex:
                                    typeof v.releaseVersionIndex === 'number' ? v.releaseVersionIndex : undefined,
                            };
                        });
                    } catch {
                        /* project-row fields still apply */
                    }
                }

                const gateInput: ProposalSigningGateInput = mergePortalReleasesIntoSigningGateInput(
                    {
                        modelUrl: project.modelUrl,
                        modelImageUrl: project.modelImageUrl,
                        conceptElevationUrl: project.conceptElevationUrl,
                        modelStepSkipped: project.modelStepSkipped,
                        conceptApprovalFlowDisabled: project.conceptApprovalFlowDisabled,
                        modelApproval: project.modelApproval,
                        modelApprovalHistory: project.modelApprovalHistory,
                        conceptApproval: project.conceptApproval,
                        conceptApprovalHistory: project.conceptApprovalHistory,
                        modelReleaseId:
                            typeof projectRaw?.modelSourceReleaseId === 'string'
                                ? projectRaw.modelSourceReleaseId
                                : undefined,
                        conceptReleaseId: project.conceptElevationSourceReleaseId,
                    },
                    releaseRows,
                );

                if (!cancelled) {
                    setPortalGateContext({
                        loading: false,
                        gateInput,
                        portalSlugForReturn: (pd.slug as string) || portalSlug || undefined,
                    });
                }
            } catch {
                if (!cancelled) {
                    setPortalGateContext({
                        loading: false,
                        gateInput: null,
                    });
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        authLoading,
        proposal,
        proposal?.dealId,
        proposal?.slug,
        portalProjectDealId,
        cameFromPortal,
        portalSlug,
        isStakeholderViewer,
    ]);

    const proposalSigningGate = useMemo(() => {
        if (isStakeholderViewer) {
            return { loading: false, canSign: false, reasons: [], blockers: [] as string[] };
        }
        if (portalGateContext.loading) {
            return { loading: true, canSign: false, reasons: [], blockers: [] as string[] };
        }
        if (!portalGateContext.gateInput) {
            return { loading: false, canSign: false, reasons: [], blockers: [] as string[] };
        }
        return {
            loading: false,
            ...evaluateProposalSigningGate({
                ...portalGateContext.gateInput,
                isRomPricing: !!proposal?.isRomPricing,
                skipConceptApproval: shouldSkipConceptApprovalForSigning({
                    flowDisabled: portalGateContext.gateInput.conceptApprovalFlowDisabled,
                    proposalStatus: proposal?.status,
                }),
            }),
        };
    }, [
        isStakeholderViewer,
        portalGateContext.loading,
        portalGateContext.gateInput,
        proposal?.isRomPricing,
        proposal?.status,
    ]);

    useEffect(() => {
        if (viewerActorType !== 'client') return;
        if (proposalSigningGate.loading || proposalSigningGate.canSign) return;
        if (!portalGateContext.gateInput) return;
        if (!shouldRedirectProposalSignToConceptModel(proposalSigningGate.reasons)) return;
        const slug = portalGateContext.portalSlugForReturn || portalSlug;
        if (!slug || slug === 'preview') return;
        if (typeof window === 'undefined' || window.location.hash !== '#proposal-signature') return;
        const projectId = portalProjectDealId || proposal?.dealId || '';
        navigate(portalConceptModelReturnPath(slug, projectId), { replace: true });
    }, [
        viewerActorType,
        proposalSigningGate.loading,
        proposalSigningGate.canSign,
        proposalSigningGate.reasons,
        portalGateContext.gateInput,
        portalGateContext.portalSlugForReturn,
        portalSlug,
        portalProjectDealId,
        proposal?.dealId,
        navigate,
    ]);

    const portalActivitySource =
        viewerActorType === 'employee'
            ? 'employee'
            : viewerActorType === 'stakeholder'
              ? 'stakeholder'
              : 'client';

    useProposalEngagementSession(
        resolvedPortalId,
        portalProjectDealId || proposal?.dealId || null,
        portalActivitySource,
        cameFromPortal && portalSlug !== 'preview' && !!resolvedPortalId && !!proposal && !authLoading,
        slug || proposal?.slug || undefined,
    );

    const logDownloadToPortalActivity = useCallback(
        async (isSigned: boolean, totalAmount: number, downloadId?: string) => {
            if (!resolvedPortalId || !cameFromPortal || portalSlug === 'preview') return;
            try {
                await logProposalDownloadToPortal({
                    portalId: resolvedPortalId,
                    projectId: portalProjectDealId || proposal?.dealId || null,
                    source: portalActivitySource,
                    proposalSlug: slug || proposal?.slug || undefined,
                    isSigned,
                    totalAmount,
                    projectName:
                        (displayProjectNameFromPortal || '').trim()
                        || (portalTabProjectNameFallback || '').trim()
                        || undefined,
                    downloadId,
                });
            } catch (paErr) {
                console.warn('[PublicProposal] Portal download activity log skipped:', paErr);
            }
        },
        [
            resolvedPortalId,
            cameFromPortal,
            portalSlug,
            portalProjectDealId,
            proposal?.dealId,
            proposal?.slug,
            portalActivitySource,
            slug,
            displayProjectNameFromPortal,
            portalTabProjectNameFallback,
        ],
    );

    /** Prefer portal tab title when client opened proposal from portal; else stored proposal name / slug fallback. */
    const headerProjectName = useMemo(() => {
        if (!proposal || proposal.isKitProposal) return '';
        const fromPortal =
            (displayProjectNameFromPortal || '').trim() ||
            (portalTabProjectNameFallback || '').trim();
        if (fromPortal) return fromPortal;
        if (proposal.projectName?.trim()) return proposal.projectName.trim();
        const num = (linkedDeal?.projectNumber || proposal.projectNumber || '').trim();
        const desc = (linkedDeal?.descriptor || proposal.descriptor || '').trim();
        if (num || desc) return [num, desc].filter(Boolean).join(' ').trim();
        const clientParts = (proposal.clientName || '').toLowerCase().replace(/\s+/g, '-');
        const slugWithoutClient = proposal.slug?.replace(clientParts + '-', '') || '';
        return slugWithoutClient.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    }, [proposal, displayProjectNameFromPortal, portalTabProjectNameFallback, linkedDeal]);

    const headerClientName = useMemo(() => {
        if (!proposal) return '';
        if (isSampleGcDemo && stakeholderDisplayName?.trim()) {
            return stakeholderDisplayName.trim();
        }
        const fromLead = (linkedLeadIdentity?.name || '').trim();
        const fromPortal = (portalClientIdentity?.name || '').trim();
        const fromDeal =
            (linkedDeal?.clientLabel || '').trim()
            || (linkedDeal?.contactName || '').trim();
        const fromProposal = (proposal.clientName || '').trim();
        return fromLead || fromPortal || fromDeal || fromProposal;
    }, [proposal, linkedDeal, linkedLeadIdentity, portalClientIdentity, isSampleGcDemo, stakeholderDisplayName]);

    const headerLeadCompany = useMemo(() => {
        if (!proposal) return '';
        if (isSampleGcDemo) {
            if (stakeholderCompanyName?.trim()) return stakeholderCompanyName.trim();
            // Invite without company should not fall back to the demo "Sample GC Partners".
            if (stakeholderDisplayName?.trim()) return '';
        }
        const fromLead = (linkedLeadIdentity?.company || '').trim();
        const fromPortal = (portalClientIdentity?.company || '').trim();
        const fromDeal = (linkedDeal?.company || '').trim();
        const fromProposal = (proposal.leadCompany || '').trim();
        return fromLead || fromPortal || fromDeal || fromProposal;
    }, [proposal, linkedDeal, linkedLeadIdentity, portalClientIdentity, isSampleGcDemo, stakeholderCompanyName, stakeholderDisplayName]);

    const pdfPreparedFor = useMemo(
        () => ({
            name: headerClientName || null,
            company: headerLeadCompany || null,
        }),
        [headerClientName, headerLeadCompany],
    );

    const headerProjectNumber = (
        linkedDeal?.projectNumber
        || proposal?.projectNumber
        || ''
    ).trim();
    const headerProjectDescriptor = useMemo(() => {
        if (!proposal || proposal.isKitProposal) return '';
        const desc = (linkedDeal?.descriptor || proposal.descriptor || '').trim();
        if (desc) return desc;
        const line = headerProjectName.trim();
        if (!line) return '';
        if (headerProjectNumber && line.startsWith(headerProjectNumber)) {
            return line.slice(headerProjectNumber.length).trim();
        }
        return line;
    }, [proposal, linkedDeal, headerProjectName, headerProjectNumber]);


    // Open lightbox with images
    const openLightbox = (images: Array<{ url: string; name?: string }>, startIndex = 0) => {
        setLightboxImages(images);
        setLightboxIndex(startIndex);
        setLightboxOpen(true);
        document.body.style.overflow = 'hidden';
    };

    // Close lightbox
    const closeLightbox = () => {
        setLightboxOpen(false);
        document.body.style.overflow = '';
    };

    // Navigate lightbox
    const nextImage = () => setLightboxIndex(prev => (prev + 1) % lightboxImages.length);
    const prevImage = () => setLightboxIndex(prev => (prev - 1 + lightboxImages.length) % lightboxImages.length);

    // Load settings
    useEffect(() => {
        const loadSettings = async () => {
            await ensureAuth();
            const settings = await getPublicSettings();
            if (settings?.engagementLogo) {
                setCompanyLogo(settings.engagementLogo);
            }
            if (settings?.engagementBackground) {
                setCompanyBackground(settings.engagementBackground);
            }
            if (settings?.engagementVideoUrl) {
                setVideoUrl(settings.engagementVideoUrl);
            }
        };
        loadSettings();
    }, []);

    // Primary client portal navigation: drop stale stakeholder claims
    useEffect(() => {
        if (authLoading || isStaffPortalUser) return;
        if (cameFromPortal && !stakeholderTokenParam) {
            clearStakeholderSessionCallable().catch(() => {});
        }
    }, [authLoading, isStaffPortalUser, cameFromPortal, stakeholderTokenParam]);

    // Fetch proposal
    useEffect(() => {
        const fetchProposal = async () => {
            if (!slug) {
                setError('Invalid proposal URL');
                setLoading(false);
                return;
            }
            if (authLoading) return;

            try {
                setAccessDenied(false);
                await ensureAuth();
                // Guests cannot list proposalBuilders — slug resolve is server-shaped.
                let loaded: { id: string; proposal: ProposalBuilderData };
                try {
                    const res = await getProposalBuilderBySlug(slug, {
                        stakeholderToken: stakeholderTokenParam || null,
                    });
                    loaded = { id: res.id, proposal: res.proposal as unknown as ProposalBuilderData };
                } catch (slugErr: unknown) {
                    const code = (slugErr as { code?: string })?.code || '';
                    if (code.includes('permission-denied')) {
                        setAccessDenied(true);
                        setLoading(false);
                        return;
                    }
                    if (code.includes('not-found')) {
                        setError('Proposal not found');
                        setLoading(false);
                        return;
                    }
                    throw slugErr;
                }
                const data = loaded.proposal;
                const proposalDocId = loaded.id;

                // Access gating: always resolve invite tokens for personalization.
                // Staff preview reads the link doc (never verifyStakeholderToken — that overwrites CRM claims).
                // Invitees verify via callable so live onSnapshot keeps working under claims.
                {
                    const sessionKey = `proposal_verified_${slug}`;
                    let isVerified = !!sessionStorage.getItem(sessionKey);

                    if (stakeholderTokenParam) {
                        try {
                            if (isStaffPortalUser) {
                                const linkSnap = await getDoc(
                                    doc(db, 'portalStakeholderLinks', stakeholderTokenParam),
                                );
                                if (!linkSnap.exists()) {
                                    setAccessDenied(true);
                                    setLoading(false);
                                    return;
                                }
                                const link = linkSnap.data() as {
                                    displayName?: string;
                                    companyName?: string;
                                    leadId?: string;
                                    revokedAt?: unknown;
                                };
                                if (link.revokedAt) {
                                    setAccessDenied(true);
                                    setLoading(false);
                                    return;
                                }
                                const inviteeName = String(link.displayName || '').trim();
                                const inviteeCompany = String(link.companyName || '').trim();
                                setStakeholderDisplayName(inviteeName || null);
                                setStakeholderCompanyName(inviteeCompany || null);
                                setStakeholderLeadId(link.leadId || null);
                                setStakeholderLinkId(stakeholderTokenParam);
                                sessionStorage.setItem(sessionKey, 'true');
                                if (inviteeName) sessionStorage.setItem(`proposal_invitee_name_${slug}`, inviteeName);
                                if (inviteeCompany) {
                                    sessionStorage.setItem(`proposal_invitee_company_${slug}`, inviteeCompany);
                                }
                                isVerified = true;
                            } else {
                                const res = await verifyStakeholderTokenCallable({
                                    token: stakeholderTokenParam,
                                    proposalId: proposalDocId,
                                });
                                const inviteeName = String(res.displayName || '').trim();
                                const inviteeCompany = String(res.companyName || '').trim();
                                setStakeholderDisplayName(inviteeName || null);
                                setStakeholderCompanyName(inviteeCompany || null);
                                setStakeholderLeadId(res.leadId || null);
                                setStakeholderLinkId(stakeholderTokenParam);
                                sessionStorage.setItem(sessionKey, 'true');
                                if (inviteeName) sessionStorage.setItem(`proposal_invitee_name_${slug}`, inviteeName);
                                if (inviteeCompany) {
                                    sessionStorage.setItem(`proposal_invitee_company_${slug}`, inviteeCompany);
                                }
                                isVerified = true;
                            }
                        } catch (stakeErr) {
                            console.error('[Proposal] Stakeholder verify failed:', stakeErr);
                            setAccessDenied(true);
                            setLoading(false);
                            return;
                        }
                    } else if (!isStaffPortalUser) {
                        const c = await readStakeholderClaimsFromToken();
                        if (c.isStakeholder && c.portalId) {
                            const portalSnap = await getDoc(doc(db, 'clientPortals', c.portalId));
                            if (portalSnap.exists()) {
                                const pd = portalSnap.data() as { dealId?: string; projects?: Record<string, unknown> };
                                const pid = proposalDocId;
                                const inPortal =
                                    pid === pd.dealId || (pd.projects != null && pid in pd.projects);
                                if (inPortal && c.displayName) {
                                    setStakeholderDisplayName(c.displayName);
                                    setStakeholderCompanyName(c.companyName);
                                    setStakeholderLeadId(c.leadId);
                                    setStakeholderLinkId(c.linkId);
                                    sessionStorage.setItem(sessionKey, 'true');
                                    isVerified = true;
                                }
                            }
                        }
                    } else if (isSampleGcProposalSlug(slug)) {
                        const cachedName = sessionStorage.getItem(`proposal_invitee_name_${slug}`);
                        const cachedCompany = sessionStorage.getItem(`proposal_invitee_company_${slug}`);
                        if (cachedName) setStakeholderDisplayName(cachedName);
                        if (cachedCompany) setStakeholderCompanyName(cachedCompany);
                    }

                    if (!isStaffPortalUser) {
                        if (!isVerified && !cameFromPortal) {
                            setAccessDenied(true);
                            setLoading(false);
                            return;
                        }
                        if (cameFromPortal && !sessionStorage.getItem(sessionKey)) {
                            sessionStorage.setItem(sessionKey, 'true');
                        }
                    }
                }

                setProposal(data);
                setProposalId(proposalDocId);

                const sel = (data as any).installSelection;
                if (typeof sel === 'boolean') {
                    setIncludeInstall(sel);
                } else if (data.status === 'approved' || (data as any).signature) {
                    const inst = (data.speciesPricing as any)?.installTotal;
                    if (typeof inst === 'number') setIncludeInstall(inst > 0);
                }

                // Load saved species selections if they exist
                const savedSpecies = (data as any).selectedSpecies as Record<string, string> | undefined;
                if (savedSpecies) {
                    setSelectedSpecies(savedSpecies);
                }

                const defaultStructureSelections = isPhasedProposal(data)
                    // Phased proposals: only committed/offered phases price into the signing total.
                    ? buildPhaseStructureSelections(data)
                    : buildDefaultStructureSelections(data);
                const parsedStructureSelections = parseStructureSelectionsForPublicTotals(data);
                setStructureSelections(
                    parsedStructureSelections
                        ? { ...defaultStructureSelections, ...parsedStructureSelections }
                        : defaultStructureSelections,
                );

                // Initialize kit species selection from activeSpecies or first offered species
                if (data.isKitProposal && (data.offeredSpecies || data.kitAvailableSpecies)) {
                    const initialKitSpecies = data.activeSpecies || data.offeredSpecies?.[0] || data.kitAvailableSpecies?.[0];
                    if (initialKitSpecies) {
                        setKitSelectedSpecies(initialKitSpecies);

                        // Update deal value with initial species pricing (if not already set correctly)
                        if (data.dealId && data.kitPricing && data.kitPricing.length > 0) {
                            const selectedPricing = data.kitPricing.find(kp => kp.speciesName === initialKitSpecies);
                            if (selectedPricing) {
                                let newDealValue = selectedPricing.totalCost;

                                // Add install if included (default is true unless installOptional is set)
                                const shouldIncludeInstallValue = !data.installOptional;
                                if (shouldIncludeInstallValue) {
                                    let installRawTotal = 0;
                                    data.nodeSets.forEach(ns => {
                                        const qty = ns.quantity || 1;
                                        installRawTotal += (ns.install.total + ns.travel.total) * qty;
                                    });
                                    const installBillable = Math.round(applyProfitMargin(installRawTotal, data.profitMargin));
                                    newDealValue += installBillable;
                                }

                                // Update deal value
                                updateDoc(doc(db, 'deals', data.dealId), {
                                    value: newDealValue,
                                    updatedAt: Timestamp.now(),
                                }).catch(err => console.error('Error updating initial deal value:', err));
                            }
                        }
                    }
                }

                // Activity logging is deferred to a separate effect that waits for auth
            } catch (err) {
                console.error('Error fetching proposal:', err);
                setError('Failed to load proposal');
            } finally {
                setLoading(false);
            }
        };

        fetchProposal();
    }, [slug, isStaffPortalUser, cameFromPortal, stakeholderTokenParam, authLoading]);

    /** Live updates (e.g. admin removes install after sign) so public totals stay in sync without a manual refresh. */
    useEffect(() => {
        if (!proposalId) return;
        const unsub = onSnapshot(
            doc(db, 'proposalBuilders', proposalId),
            (snap) => {
                if (!snap.exists()) return;
                setProposal(snap.data() as ProposalBuilderData);
            },
            (err) => console.error('[PublicProposal] proposal snapshot error:', err),
        );
        return () => unsub();
    }, [proposalId]);

    /** Phased projects: keep unsigned selections in sync when staff offer/reprice phases live. */
    const phasesKey = useMemo(
        () => (proposal?.phases || []).map(p => `${p.id}:${p.status}`).join('|'),
        [proposal?.phases],
    );
    useEffect(() => {
        if (!proposal || !isPhasedProposal(proposal)) return;
        const signed = proposal.status === 'approved' || !!(proposal as { signature?: unknown }).signature;
        if (signed) return;
        setStructureSelections(prev => {
            const base = buildPhaseStructureSelections(proposal);
            // Preserve the client's optional-structure opt-outs within signable phases.
            proposal.nodeSets.forEach(ns => {
                if (ns.clientOptional === true && base[ns.id] !== false && prev[ns.id] === false) {
                    base[ns.id] = false;
                }
            });
            const exclusiveMembers = listExclusiveStructures(proposal.nodeSets);
            const allowed = exclusiveMembers.filter(m => base[m.id] !== false);
            const prevPick = allowed.find(m => prev[m.id] !== false);
            if (prevPick) {
                for (const member of exclusiveMembers) {
                    if (base[member.id] === false) continue;
                    base[member.id] = member.id === prevPick.id;
                }
            }
            return normalizeExclusiveGroupSelections(proposal.nodeSets, base);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phasesKey]);

    /**
     * Change orders: phased proposals use them for later-phase commitments,
     * and signed proposals need approved COs for the payment schedule breakdown.
     */
    useEffect(() => {
        if (!proposal?.dealId) return;
        const proposalSigned = proposal.status === 'approved' || !!(proposal as { signature?: unknown }).signature;
        if (!isPhasedProposal(proposal) && !proposalSigned) return;
        const unsub = subscribeChangeOrdersForDeal(proposal.dealId, setDealChangeOrders);
        return () => unsub();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [proposal?.dealId, proposal?.phasingEnabled, proposal?.status]);

    /** Portal "Review & commit" / "Sign Proposal" links land on #phase-* or #proposal-signature. */
    const phaseAnchorScrolledRef = useRef(false);
    useEffect(() => {
        if (!proposal || phaseAnchorScrolledRef.current) return;
        const hash = window.location.hash;
        const isPhase = hash.startsWith('#phase-');
        const isSignature = hash === '#proposal-signature';
        if (!isPhase && !isSignature) return;
        phaseAnchorScrolledRef.current = true;
        window.setTimeout(() => {
            document.getElementById(hash.slice(1))?.scrollIntoView({
                behavior: 'smooth',
                block: isSignature ? 'start' : 'center',
            });
        }, 500);
    }, [proposal]);

    useEffect(() => {
        if (!proposal?.dealId || !proposalId) {
            setLinkedDeal(null);
            return;
        }
        let cancelled = false;
        let unsubscribe: (() => void) | undefined;
        void (async () => {
            try {
                const crmDealId = await resolveEnhancedDealIdForProposalDoc(db, proposalId, proposal.dealId);
                if (cancelled) return;
                const dealRef = doc(db, 'enhancedDeals', crmDealId || proposal.dealId);
                unsubscribe = onSnapshot(dealRef, (snap) => {
                    if (!snap.exists()) {
                        setLinkedDeal(null);
                        return;
                    }
                    const data = snap.data() as EnhancedDeal;
                    setLinkedDeal({
                        projectNumber: data.projectNumber,
                        clientLabel: data.clientLabel,
                        descriptor: data.descriptor,
                        title: data.title,
                        contactName: data.contactName,
                        company: data.company,
                        leadId: data.leadId,
                    });
                }, (err) => {
                    console.error('[PublicProposal] linked deal listener failed:', err);
                    setLinkedDeal(null);
                });
            } catch (err) {
                console.error('[PublicProposal] failed to load linked deal naming:', err);
                if (!cancelled) setLinkedDeal(null);
            }
        })();
        return () => {
            cancelled = true;
            unsubscribe?.();
        };
    }, [proposal?.dealId, proposalId]);

    useEffect(() => {
        const leadId = linkedDeal?.leadId;
        if (!leadId) {
            setLinkedLeadIdentity(null);
            return;
        }
        return onSnapshot(
            doc(db, 'leads', leadId),
            (snap) => {
                if (!snap.exists()) {
                    setLinkedLeadIdentity(null);
                    return;
                }
                const data = snap.data() as { firstName?: string; lastName?: string; company?: string };
                const name = [data.firstName, data.lastName].filter(Boolean).join(' ').trim();
                const company = (data.company || '').trim();
                setLinkedLeadIdentity({
                    name: name || undefined,
                    company: company || undefined,
                });
            },
            () => setLinkedLeadIdentity(null),
        );
    }, [linkedDeal?.leadId]);

    // Log proposal view activity AFTER auth has resolved
    // This ensures we know if the viewer is an employee or client
    useEffect(() => {
        if (authLoading || !proposal || !proposalId || viewLoggedRef.current) return;

        viewLoggedRef.current = true;

        const logViewActivity = async () => {
            if (!proposal.dealId) return;

            try {
                await addDoc(collection(db, 'activities'), {
                    type: 'note',
                    title: 'Proposal Viewed',
                    description: `${viewerActorName || proposal.clientName || 'Client'} opened the proposal.`,
                    entityType: 'deal',
                    entityId: proposal.dealId,
                    entityName: proposal.clientName,
                    status: 'completed',
                    completedAt: Timestamp.now(),
                    createdBy: 'system',
                    createdAt: Timestamp.now(),
                    updatedAt: Timestamp.now(),
                    actorType: viewerActorType,
                    actorName: viewerActorName || proposal.clientName || 'Client',
                });
            } catch (actErr) {
                console.error('Error creating view activity:', actErr);
            }

            const isStaffPreviewFrame = portalSlug === 'preview';
            if (cameFromPortal && portalSlug && !isStaffPreviewFrame) {
                try {
                    const viewedProposalKey = `lctf_viewed_proposal:${proposalId}`;
                    if (sessionStorage.getItem(viewedProposalKey)) {
                        return;
                    }
                    const accessMeta = await getPortalAccessMeta(portalSlug);
                    const portalDoc = await getDoc(doc(db, 'clientPortals', accessMeta.portalId));
                    if (portalDoc.exists()) {
                        const projectId =
                            portalProjectDealId
                            || proposal.dealId
                            || portalDoc.data()?.dealId
                            || null;
                        const activitySource =
                            viewerActorType === 'employee'
                                ? 'employee'
                                : viewerActorType === 'stakeholder'
                                  ? 'stakeholder'
                                  : 'client';
                        await logPortalActivity(
                            portalDoc.id,
                            projectId,
                            'link_click',
                            'viewed_proposal',
                            {
                                proposalSlug: slug || proposal.slug || '',
                                projectName:
                                    (displayProjectNameFromPortal || '').trim()
                                    || portalTabProjectNameFallback
                                    || undefined,
                            },
                            activitySource,
                        );
                        sessionStorage.setItem(viewedProposalKey, new Date().toISOString());
                    }
                } catch (paErr) {
                    console.warn('[PublicProposal] Portal activity log skipped:', paErr);
                }
            }

            // Update status to 'opened' on first client view.
            // Guards (all required):
            //   - Status was 'sent' (the only state we transition out of here)
            //   - Viewer resolved to 'client' (not employee, not stakeholder)
            if (
                proposal.status === 'sent'
                && viewerActorType === 'client'
                && !isStakeholderViewer
                && !isStaffPreviewFrame
            ) {
                try {
                    await updateDoc(doc(db, 'proposalBuilders', proposalId), {
                        status: 'opened',
                        openedAt: Timestamp.now(),
                    });
                } catch (statusErr) {
                    console.warn('Could not update proposal status:', statusErr);
                }
            }
        };

        logViewActivity();
    }, [authLoading, proposal, proposalId, viewerActorType, viewerActorName]);

    // Format currency
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };


    const enabledNodeSets = useMemo(
        () => proposal?.nodeSets.filter(ns => ns.enabled !== false) ?? [],
        [proposal]
    );

    const effectiveStructureSelections = useMemo(() => {
        if (!proposal) return {};
        const signed = proposal.status === 'approved' || !!(proposal as { signature?: unknown }).signature;
        if (signed) {
            const parsed = parseStructureSelectionsForPublicTotals(proposal);
            return parsed ?? buildDefaultStructureSelections(proposal);
        }
        return structureSelections;
    }, [proposal, structureSelections]);

    const clientVisibleNodeSets = useMemo(
        () => (proposal ? getClientVisibleNodeSets(proposal, effectiveStructureSelections) : []),
        [proposal, effectiveStructureSelections],
    );

    const signedLayoutSnapshot = useMemo(
        () => (proposal ? getAppliedSignedStructureSnapshot(proposal) : null),
        [proposal],
    );

    /** Structures that existed at signing. Later additions stay off the client page and PDF. */
    const layoutNodeSets = useMemo(
        () => (proposal ? filterToSignedProposalLayout(proposal, enabledNodeSets) : []),
        [proposal, enabledNodeSets],
    );

    // Check if any contracted structure has multiple species configured
    const hasMultipleSpecies = useMemo(() => {
        return clientVisibleNodeSets.some(ns =>
            ns.materials.quoteMultipleSpecies &&
            (ns.materials.alternateSpecies?.length ?? 0) > 0
        );
    }, [clientVisibleNodeSets]);

    // Phased display ordering: phase order first, then structure name.
    // Non-phased proposals keep their builder-defined order untouched.
    const proposalIsPhased = !!proposal && isPhasedProposal(proposal);

    const sortedGalleryNodeSets = useMemo(() => {
        if (!proposal || !proposalIsPhased) return layoutNodeSets;
        const orderById = new Map(getOrderedPhases(proposal).map((p, i) => [p.id, i]));
        return [...layoutNodeSets].sort((a, b) => {
            const pa = orderById.get(getNodeSetPhaseId(proposal, a) || '') ?? 0;
            const pb = orderById.get(getNodeSetPhaseId(proposal, b) || '') ?? 0;
            if (pa !== pb) return pa - pb;
            return (a.name || '').localeCompare(b.name || '');
        });
    }, [proposal, proposalIsPhased, layoutNodeSets]);

    /**
     * Cost-breakdown groups for the investment buckets. Non-phased proposals
     * get a single unlabeled group (original order/behavior); phased proposals
     * get one group per phase (phase order, structures alphabetical) filtered
     * to client-visible structures.
     */
    const breakdownGroups = useMemo((): Array<{ phase: ProposalPhase | null; nodeSets: ProposalNodeSet[] }> => {
        if (!proposal) return [];
        if (!proposalIsPhased) return [{ phase: null, nodeSets: clientVisibleNodeSets }];
        const visibleIds = new Set(clientVisibleNodeSets.map(ns => ns.id));
        return groupNodeSetsByPhase(proposal)
            .map(({ phase, nodeSets }) => ({
                phase,
                nodeSets: nodeSets
                    .filter(ns => visibleIds.has(ns.id))
                    .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
            }))
            .filter(g => g.nodeSets.length > 0);
    }, [proposal, proposalIsPhased, clientVisibleNodeSets]);

    const optionalStructures = useMemo(
        () => layoutNodeSets.filter(ns => ns.clientOptional === true),
        [layoutNodeSets],
    );

    const exclusiveStructures = useMemo(
        () => listExclusiveStructures(layoutNodeSets),
        [layoutNodeSets],
    );

    const showStructuresGallery = useMemo(() => {
        if (!proposal) return false;
        return (
            layoutNodeSets.some(ns => ns.images && ns.images.length > 0)
            || (showTimberSizes && layoutNodeSets.some(ns => (timberSummariesByStructureId[ns.id] || []).length > 0))
            || optionalStructures.length > 0
            || exclusiveStructures.length > 1
        );
    }, [proposal, layoutNodeSets, showTimberSizes, timberSummariesByStructureId, optionalStructures.length, exclusiveStructures.length]);

    const kitOfferedSpeciesCount = proposal?.offeredSpecies?.length ?? 0;
    /** Kit timber row can expand when multiple species offered or multiple structures (single species still shows per-structure dropdowns). */
    const kitTimberBucketExpandable =
        !!proposal?.isKitProposal && (kitOfferedSpeciesCount > 1 || clientVisibleNodeSets.length > 1);
    const singleEnabledStructure = clientVisibleNodeSets.length === 1;
    const showKitSpeciesBreakdown =
        !!proposal?.isKitProposal &&
        (kitOfferedSpeciesCount > 0 || !!proposal.activeSpecies) &&
        (expandedBuckets['timberKit'] || !kitTimberBucketExpandable);
    const showCustomTimberBreakdown =
        !!proposal &&
        !proposal.isKitProposal &&
        (singleEnabledStructure ||
            (expandedBuckets['timberKit'] && (clientVisibleNodeSets.length > 1 || hasMultipleSpecies)));
    const timberKitDetailsOpen =
        expandedBuckets['timberKit'] || showKitSpeciesBreakdown || showCustomTimberBreakdown;

    // Build species options with pricing
    const speciesOptions = useMemo(() => {
        if (!proposal || !hasMultipleSpecies) return [];

        // Get primary species name (from first timber item of first nodeSet)
        const primarySpecies = clientVisibleNodeSets[0]?.materials.timberList[0]?.species || 'Default';

        // Calculate default totals - use billablePrices if available
        const defaultTotal = clientVisibleNodeSets.reduce((sum, ns) => {
            const buckets = calculateProposalNodeBucketsWithSelections(
                ns,
                proposal.profitMargin,
                proposal,
                effectiveStructureSelections,
            );
            return sum + buckets.total;
        }, 0);

        const options = [{ name: primarySpecies, total: defaultTotal, isDefault: true }];

        // Get all alternate species from all nodeSets
        const alternateNames = new Set<string>();
        clientVisibleNodeSets.forEach(ns => {
            (ns.materials.alternateSpecies || []).forEach(s => {
                alternateNames.add(s.speciesName);
            });
        });

        // Calculate totals for each alternate species
        alternateNames.forEach(speciesName => {
            const speciesTotal = clientVisibleNodeSets.reduce((sum, ns) => {
                const buckets = calculateProposalNodeBucketsWithSelections(
                    ns,
                    proposal.profitMargin,
                    proposal,
                    effectiveStructureSelections,
                );

                const timberKit = speciesTimberKitBillable(
                    ns,
                    speciesName,
                    buckets.timberKit,
                    proposal.profitMargin,
                    ns.quantity || 1,
                    ns.billablePrices?.speciesOptions,
                );
                return sum + buckets.design + timberKit + buckets.install;
            }, 0);
            options.push({ name: speciesName, total: speciesTotal, isDefault: false });
        });

        return options;
    }, [proposal, hasMultipleSpecies, clientVisibleNodeSets, effectiveStructureSelections]);

    const effectiveIncludeInstall = useMemo(() => {
        if (!proposal) return true;
        return getEffectiveIncludeInstallForContractDisplay(proposal, includeInstall);
    }, [proposal, includeInstall]);

    const grandTotals = useMemo(() => {
        if (!proposal) return { design: 0, timberKit: 0, install: 0, travel: 0, bonding: 0, bondingPercent: 0, discountAmount: 0, total: 0 };

        const signedTotals = getSignedGrandTotalsDisplay(proposal);
        if (signedTotals) return signedTotals;

        return computeProposalGrandTotals(proposal, {
            includeInstall: effectiveIncludeInstall,
            selectedSpecies,
            kitSelectedSpecies,
            structureSelections: effectiveStructureSelections,
        });
    }, [proposal, selectedSpecies, effectiveIncludeInstall, kitSelectedSpecies, effectiveStructureSelections]);

    const publicCostPerSqFt = useMemo(() => {
        if (!proposal?.publicSettings?.showCostPerSqFt) return null;
        return projectCostPerSqFt(grandTotals.total, proposal.projectSquareFootage);
    }, [proposal, grandTotals.total]);

    /**
     * Promotional discount line above the total. The displayed total is already
     * discounted (live via computeProposalGrandTotals, signed via the sign-time
     * snapshot), so this line is informational.
     */
    const discountLine = useMemo(() => {
        const d = proposal?.discount;
        if (!d?.enabled) return null;
        const amount = Math.round(Number(d.amount) || 0);
        if (amount <= 0) return null;
        return {
            label: d.label?.trim() || 'Discount',
            amount,
            description: d.clientDescription?.trim() || '',
        };
    }, [proposal]);

    /**
     * Payment schedule: base 50/40/10 of the signing total, with approved
     * change orders either folded into remaining installments or shown as
     * their own due-at-approval lines.
     */
    const paymentScheduleBreakdown = useMemo(
        () => buildPaymentScheduleBreakdown(grandTotals.total, dealChangeOrders, {
            invoicedInstallments: invoicedInstallmentLocks(installmentMirror),
        }),
        [grandTotals.total, dealChangeOrders, installmentMirror],
    );

    const paymentInstallmentStatuses = useMemo(() => {
        if (!installmentMirror?.length) return undefined;
        const map: Record<string, {
            status: string;
            paidAt?: Date;
            invoicedAt?: Date;
            amountBilled?: number;
            amountPaid?: number;
            amountUnbilled?: number;
            partiallyBilled?: boolean;
            structureGroupLabels?: string[];
        }> = {};
        for (const p of installmentMirror) {
            map[p.installmentId] = {
                status: p.status,
                paidAt: p.paidAt,
                invoicedAt: p.invoicedAt,
                amountBilled: p.amountBilled,
                amountPaid: p.amountPaid,
                amountUnbilled: p.amountUnbilled,
                partiallyBilled: p.partiallyBilled,
                structureGroupLabels: p.structureGroupLabels,
            };
        }
        return map;
    }, [installmentMirror]);

    /**
     * Per-phase totals for the total block (phased proposals only). Each row is
     * a phase with client-visible structures; amounts sum the phase's structure
     * buckets so the rows add up to the signing total shown beneath them.
     */
    const signingPhaseRows = useMemo((): Array<{ phase: ProposalPhase; amount: number }> => {
        if (!proposal || !proposalIsPhased) return [];
        return breakdownGroups
            .filter((g): g is { phase: ProposalPhase; nodeSets: ProposalNodeSet[] } => g.phase !== null)
            .map(({ phase, nodeSets }) => ({
                phase,
                amount: nodeSets.reduce((sum, ns) => {
                    const buckets = calculateProposalNodeBucketsWithSelections(
                        ns, proposal.profitMargin, proposal, effectiveStructureSelections,
                    );
                    return sum + buckets.design + buckets.timberKit + (effectiveIncludeInstall ? buckets.install : 0);
                }, 0),
            }));
    }, [proposal, proposalIsPhased, breakdownGroups, effectiveStructureSelections, effectiveIncludeInstall]);

    /** Phases excluded from today's total (future / not yet committed scope). */
    const hasPhasesOutsideSigning = useMemo(() => {
        if (!proposal || !proposalIsPhased) return false;
        const inTotal = new Set(signingPhaseRows.map(r => r.phase.id));
        return getOrderedPhases(proposal).some(p => !inTotal.has(p.id));
    }, [proposal, proposalIsPhased, signingPhaseRows]);

    // Check if any nodeSet has travel costs
    const hasTravel = useMemo(() => {
        if (!proposal) return false;
        return clientVisibleNodeSets.some(ns => ns.travel.total > 0);
    }, [proposal, clientVisibleNodeSets]);

    // Toggle bucket expansion
    const toggleBucket = (bucketName: string) => {
        setExpandedBuckets(prev => ({
            ...prev,
            [bucketName]: !prev[bucketName]
        }));
    };

    // Handle species selection change - persist to Firestore
    const handleSpeciesChange = async (nodeSetId: string, speciesValue: string) => {
        const previousSpecies = selectedSpecies[nodeSetId] || 'default';
        const newSelection = {
            ...selectedSpecies,
            [nodeSetId]: speciesValue
        };
        setSelectedSpecies(newSelection);

        // Log species change activity FIRST (this is allowed for public users)
        if (proposal?.dealId && speciesValue !== previousSpecies) {
            try {
                const structureName = proposal.nodeSets?.find(ns => ns.id === nodeSetId)?.name || 'Structure';
                await addDoc(collection(db, 'activities'), {
                    type: 'note',
                    title: 'Species Changed',
                    description: `${viewerActorName || proposal.clientName || 'Client'} changed species for ${structureName} from ${previousSpecies === 'default' ? 'Default' : previousSpecies} to ${speciesValue === 'default' ? 'Default' : speciesValue}.`,
                    entityType: 'deal',
                    entityId: proposal.dealId,
                    entityName: proposal.clientName,
                    status: 'completed',
                    completedAt: Timestamp.now(),
                    createdBy: 'system',
                    createdAt: Timestamp.now(),
                    updatedAt: Timestamp.now(),
                    actorType: viewerActorType,
                    actorName: viewerActorName || proposal.clientName || 'Client',
                    changeDetails: {
                        structureId: nodeSetId,
                        structureName,
                        previousSpecies,
                        newSpecies: speciesValue,
                    },
                });
            } catch (actErr) {
                console.warn('Could not log species change activity:', actErr);
            }
        }

        // Try to persist to Firestore (may fail for unauthenticated users - that's OK)
        if (proposalId) {
            try {
                await updateDoc(doc(db, 'proposalBuilders', proposalId), {
                    selectedSpecies: newSelection,
                    updatedAt: Timestamp.now(),
                });
            } catch (err) {
                console.warn('Could not save species selection:', err);
            }
        }
    };

    // Handle kit species selection change - persist to Firestore and update deal value
    const handleKitSpeciesChange = async (speciesValue: string) => {
        const previousSpecies = kitSelectedSpecies || 'default';
        setKitSelectedSpecies(speciesValue);

        // Log activity first (this is allowed for public users)
        if (proposal?.dealId && speciesValue !== previousSpecies) {
            try {
                await addDoc(collection(db, 'activities'), {
                    type: 'note',
                    title: 'Kit Species Changed',
                    description: `${viewerActorName || proposal.clientName || 'Client'} changed kit species from ${previousSpecies} to ${speciesValue}.`,
                    entityType: 'deal',
                    entityId: proposal.dealId,
                    entityName: proposal.clientName,
                    status: 'completed',
                    completedAt: Timestamp.now(),
                    createdBy: 'system',
                    createdAt: Timestamp.now(),
                    updatedAt: Timestamp.now(),
                    actorType: viewerActorType,
                    actorName: viewerActorName || proposal.clientName || 'Client',
                });
            } catch (actErr) {
                console.warn('Could not log species change activity:', actErr);
            }
        }

        // Note: We don't update the proposalBuilders document from the public page
        // as this requires authentication. The species selection is tracked via local state
        // and the activity log above.
    };

    // Handle install toggle - with activity logging
    const handleInstallToggle = async (included: boolean) => {
        const previousValue = includeInstall;
        setIncludeInstall(included);

        // Log install toggle activity if changed
        if (proposal?.dealId && included !== previousValue) {
            try {
                await addDoc(collection(db, 'activities'), {
                    type: 'note',
                    title: 'Install Toggled',
                    description: `${viewerActorName || proposal.clientName || 'Client'} ${included ? 'included' : 'excluded'} installation from the proposal.`,
                    entityType: 'deal',
                    entityId: proposal.dealId,
                    entityName: proposal.clientName,
                    status: 'completed',
                    completedAt: Timestamp.now(),
                    createdBy: 'system',
                    createdAt: Timestamp.now(),
                    updatedAt: Timestamp.now(),
                    actorType: viewerActorType,
                    actorName: viewerActorName || proposal.clientName || 'Client',
                    changeDetails: {
                        previousValue: previousValue ? 'Included' : 'Excluded',
                        newValue: included ? 'Included' : 'Excluded',
                    },
                });
            } catch (err) {
                console.error('Error logging install toggle:', err);
            }
        }
    };

    // Handle optional structure toggle — with activity logging
    const handleStructureToggle = async (structureId: string, included: boolean) => {
        if (!proposal) return;
        const target = proposal.nodeSets.find(ns => ns.id === structureId);
        if (!target) return;

        const previousValue = structureSelections[structureId] !== false;
        let newSelections: Record<string, boolean>;

        if (structureIsExclusive(target)) {
            if (!included) return;
            const next = selectExclusiveGroupMember(proposal.nodeSets, structureSelections, structureId);
            if (!next) return;
            if (countClientSelectedStructures(proposal, next) === 0) return;
            newSelections = next;
        } else if (target.clientOptional) {
            newSelections = { ...structureSelections, [structureId]: included };
            if (!included) {
                const remaining = countClientSelectedStructures(proposal, newSelections);
                if (remaining === 0) return;
            }
        } else {
            return;
        }

        setStructureSelections(newSelections);

        if (proposal.dealId && included !== previousValue) {
            try {
                await addDoc(collection(db, 'activities'), {
                    type: 'note',
                    title: 'Structure Toggled',
                    description: `${viewerActorName || proposal.clientName || 'Client'} ${included ? 'included' : 'excluded'} "${target.name}" from the proposal.`,
                    entityType: 'deal',
                    entityId: proposal.dealId,
                    entityName: proposal.clientName,
                    status: 'completed',
                    completedAt: Timestamp.now(),
                    createdBy: 'system',
                    createdAt: Timestamp.now(),
                    updatedAt: Timestamp.now(),
                    actorType: viewerActorType,
                    actorName: viewerActorName || proposal.clientName || 'Client',
                    changeDetails: {
                        previousValue: previousValue ? 'Included' : 'Excluded',
                        newValue: included ? 'Included' : 'Excluded',
                        structureId,
                        structureName: target.name,
                    },
                });
            } catch (err) {
                console.error('Error logging structure toggle:', err);
            }
        }
    };

    // Handle signature
    const handleSign = async (signatureData: { dataUrl: string; signedBy: string }) => {
        if (!proposalId || !proposal) return;

        if (proposal.status === 'approved' || proposal.signature) {
            alert('This proposal has already been signed.');
            return;
        }

        if (proposal.isRomPricing) {
            alert('This proposal is ROM pricing only and cannot be signed.');
            return;
        }

        if (!proposalSigningGate.canSign) {
            const pending = proposalSigningGate.blockers.join('; ');
            alert(
                pending
                    ? `This proposal cannot be signed yet: ${pending}.`
                    : 'This proposal cannot be signed yet. Please return to your client portal for next steps.',
            );
            return;
        }

        try {
            await ensureAuth();
            if (!auth.currentUser) {
                throw new Error('Could not establish a secure session. Please refresh the page and try again.');
            }

            const signature = {
                dataUrl: signatureData.dataUrl,
                signedBy: signatureData.signedBy,
                signedAt: Timestamp.now(),
            };

            // Build per-structure species selections for snapshot (client-selected structures only)
            const speciesSelections: Record<string, string> = {};
            clientVisibleNodeSets.forEach(ns => {
                const selected = selectedSpecies[ns.id] || 'default';
                speciesSelections[ns.id] = selected === 'default'
                    ? (ns.materials.timberList[0]?.species || 'Default')
                    : selected;
            });

            const selectionsSnapshot = { ...effectiveStructureSelections };

            // Determine if install is included
            const shouldIncludeInstall = getEffectiveIncludeInstallForContractDisplay(proposal, includeInstall);

            const crmDealId = await resolveEnhancedDealIdForProposalDoc(db, proposalId, proposal.dealId);
            const portalDealId = crmDealId || proposal.dealId || undefined;
            let portalActivityPortalId: string | null = null;
            let portalActivityProjectId: string | null = null;

            // ==========================================
            // 1. UPDATE PROPOSAL with all selections
            // ==========================================
            const proposalSignUpdate = removeUndefinedDeep({
                signature,
                status: 'approved',
                approvedAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
                // Lock the public proposal slug forever — clients have this
                // URL once they've signed, so renaming the project must
                // never invalidate it.
                slugLocked: true,
                ...(crmDealId ? { dealId: crmDealId } : {}),
                // Keep top-level grandTotal in sync so Slack/onProposalSigned matches signed PDF + sales order
                grandTotal: grandTotals.total,
                // Save per-structure species selections and pricing snapshot
                selectedSpecies: speciesSelections,
                installSelection: shouldIncludeInstall,
                structureSelections: selectionsSnapshot,
                // Bonding premium frozen with the rest of the signed price (0 when the proposal has none).
                bondingTotal: grandTotals.bonding,
                speciesPricing: {
                    selections: speciesSelections,
                    designTotal: grandTotals.design,
                    timberTotal: grandTotals.timberKit,
                    installTotal: shouldIncludeInstall ? grandTotals.install : 0,
                    billableTotal: grandTotals.total,
                    discountAmount: grandTotals.discountAmount,
                    structureSelections: selectionsSnapshot,
                    bondingTotal: grandTotals.bonding,
                },
                // Phased projects: the signature commits every currently-offered phase.
                ...(isPhasedProposal(proposal)
                    ? {
                        phases: commitOfferedPhases(proposal.phases || [], {
                            committedBy: signatureData.signedBy,
                            signature: {
                                dataUrl: signatureData.dataUrl,
                                signedBy: signatureData.signedBy,
                                signedAt: new Date(),
                            },
                        }),
                    }
                    : {}),
            });

            await updateDoc(doc(db, 'proposalBuilders', proposalId), proposalSignUpdate);

            // ==========================================
            // 2. UPDATE LINKED DEAL to Won status
            // ==========================================
            if (crmDealId) {
                try {
                    // Check if it's an enhanced deal first
                    const enhancedDealRef = doc(db, 'enhancedDeals', crmDealId);
                    const enhancedDealSnap = await getDoc(enhancedDealRef);

                    if (enhancedDealSnap.exists()) {
                        const dealData = enhancedDealSnap.data();
                        // Already won: sync value only — never re-date closedAt / firstWonAt.
                        if (isAlreadyWonDeal(dealData)) {
                            await updateDoc(enhancedDealRef, {
                                value: grandTotals.total,
                                updatedAt: Timestamp.now(),
                            });
                        } else {
                        const now = new Date();
                        const prevEntered = dealData.currentStageEnteredAt?.toDate?.()
                            ?? dealData.createdAt?.toDate?.()
                            ?? now;
                        const durationMs = Math.max(0, now.getTime() - prevEntered.getTime());

                        let wonStageId = dealData.stageId as string;
                        const pipelineId = dealData.pipelineId as string | undefined;
                        if (pipelineId) {
                            try {
                                const pipelineSnap = await getDoc(doc(db, 'pipelines', pipelineId));
                                if (pipelineSnap.exists()) {
                                    const stages = (pipelineSnap.data().stages || []) as Array<{ id: string; name: string; order: number }>;
                                    const picked = pickWonPipelineStageId(stages);
                                    if (picked) wonStageId = picked;
                                }
                            } catch { /* keep existing stageId */ }
                        }

                        const prevHistory = Array.isArray(dealData.stageHistory) ? dealData.stageHistory : [];
                        const newTransition = {
                            fromStage: dealData.stageId,
                            toStage: wonStageId,
                            transitionedAt: Timestamp.fromDate(now),
                            durationMs,
                            transitionedBy: 'client-portal',
                        };

                        const wonAt = Timestamp.fromDate(now);
                        await updateDoc(enhancedDealRef, {
                            outcome: 'won',
                            stageId: wonStageId,
                            closedAt: wonAt,
                            ...(shouldSetFirstWonAt(dealData) ? { firstWonAt: wonAt } : {}),
                            value: grandTotals.total,
                            probability: 100,
                            updatedAt: Timestamp.now(),
                            currentStageEnteredAt: Timestamp.fromDate(now),
                            stageHistory: [...prevHistory, newTransition],
                        });
                        }
                    }
                } catch (dealErr) {
                    console.error('Error updating deal:', dealErr);
                }
            } else if (proposal.dealId) {
                try {
                    const legacyDealRef = doc(db, 'deals', proposal.dealId);
                    const legacyDealSnap = await getDoc(legacyDealRef);
                    if (legacyDealSnap.exists()) {
                        await updateDoc(legacyDealRef, {
                            stage: 'Won',
                            actualCloseDate: Timestamp.now(),
                            value: grandTotals.total,
                            updatedAt: Timestamp.now(),
                        });
                    }
                } catch (dealErr) {
                    console.error('Error updating legacy deal:', dealErr);
                }
            }

            // ==========================================
            // 2b. PORTAL Signed → Plans is server-side (onProposalSigned /
            // advancePortalToPlansOnProposalSign). Guests cannot list/update
            // clientPortals currentStage under PIN-scoped rules.
            // ==========================================
            portalActivityPortalId = resolvedPortalId;
            if (!portalActivityPortalId && cameFromPortal && portalSlug && portalSlug !== 'preview') {
                try {
                    const accessMeta = await getPortalAccessMeta(portalSlug);
                    portalActivityPortalId = accessMeta.portalId;
                } catch (metaErr) {
                    console.error(
                        '[Proposal] Could not resolve portalId for activity after sign (guest list queries denied):',
                        metaErr,
                    );
                }
            }
            if (!portalActivityPortalId) {
                console.warn(
                    '[Proposal] No portalId for post-sign activity; stage advance relies on onProposalSigned server path.',
                );
            }
            portalActivityProjectId = portalDealId || proposal.dealId || null;

            // ==========================================
            // 2c. Timber timeline + precon → contracted (deal won + sales order handled above/below)
            // ==========================================
            const signDealId = crmDealId || portalDealId || proposal.dealId;
            if (signDealId) {
                try {
                    await syncSignedDealSideEffects(signDealId, 'client-portal', {
                        skipMarkWon: true,
                        skipSalesOrder: false,
                        proposalDocId: proposalId,
                    });
                } catch (tlErr) {
                    console.error('Error syncing signed deal timber/timeline:', tlErr);
                }
            }

            // ==========================================
            // 3. CREATE ACTIVITY (CRM Notification Center fan-out runs in onProposalSigned Cloud Function)
            // ==========================================
            const signDescription = `${viewerActorName || proposal.clientName || 'Client'} signed the proposal for ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(grandTotals.total)}. ${shouldIncludeInstall ? 'Includes installation.' : 'Installation not included.'}${proposal.isKitProposal ? ' (Kit Proposal)' : ''}`;
            try {
                await addDoc(collection(db, 'activities'), {
                    type: 'note',
                    title: 'Proposal Signed!',
                    description: signDescription,
                    entityType: portalDealId ? 'deal' : 'proposal',
                    entityId: portalDealId || proposalId,
                    entityName: proposal.clientName,
                    status: 'completed',
                    completedAt: Timestamp.now(),
                    createdBy: 'system',
                    createdAt: Timestamp.now(),
                    updatedAt: Timestamp.now(),
                    actorType: viewerActorType,
                    actorName: viewerActorName || proposal.clientName || 'Client',
                    isKitProposal: proposal.isKitProposal || false,
                });
            } catch (actErr) {
                console.error('Error creating activity:', actErr);
            }

            // Sales order (progress billing) is created in syncSignedDealSideEffects via
            // ensureBaseProposalSalesOrder — do not duplicate a non-progress order here.

            if (portalActivityPortalId) {
                try {
                    const activitySource =
                        viewerActorType === 'employee'
                            ? 'employee'
                            : viewerActorType === 'stakeholder'
                              ? 'stakeholder'
                              : 'client';
                    await logPortalActivity(
                        portalActivityPortalId,
                        portalActivityProjectId,
                        'action',
                        'signed_construction_proposal',
                        {
                            signedBy: signatureData.signedBy,
                            grandTotal: grandTotals.total,
                            proposalSlug: slug || proposal.slug || '',
                        },
                        activitySource,
                    );
                } catch (paErr) {
                    console.warn('[PublicProposal] Portal activity log skipped:', paErr);
                }
            }

            // ==========================================
            // UPDATE LOCAL STATE
            // ==========================================
            setProposal(prev => prev ? {
                ...prev,
                signature: { ...signature, signedAt: new Date() },
                status: 'approved',
                approvedAt: new Date(),
                slugLocked: true,
            } as ProposalBuilderData : null);

            // ==========================================
            // 5. GENERATE SIGNED PDF automatically
            // ==========================================
            try {
                // Fetch company logo
                let logoBase64: string | undefined;
                try {
                    const settings = await getPublicSettings();
                    if (settings.engagementLogo?.url) {
                        const logoUrl = settings.engagementLogo.url.includes('?')
                            ? `${settings.engagementLogo.url}&t=${Date.now()}`
                            : `${settings.engagementLogo.url}?t=${Date.now()}`;
                        const response = await fetch(logoUrl, { mode: 'cors', cache: 'no-store', credentials: 'omit' });
                        if (response.ok) {
                            const blob = await response.blob();
                            logoBase64 = await new Promise<string>((resolve, reject) => {
                                const reader = new FileReader();
                                reader.onloadend = () => resolve(reader.result as string);
                                reader.onerror = () => reject(new Error('FileReader failed'));
                                reader.readAsDataURL(blob);
                            });
                        }
                    }
                } catch {
                    // Try fallback logo
                    try {
                        const fallbackResponse = await fetch('/assets/logo/LC_pdf_logo.png');
                        if (fallbackResponse.ok) {
                            const fallbackBlob = await fallbackResponse.blob();
                            logoBase64 = await new Promise<string>((resolve, reject) => {
                                const reader = new FileReader();
                                reader.onloadend = () => resolve(reader.result as string);
                                reader.onerror = () => reject(new Error('FileReader failed'));
                                reader.readAsDataURL(fallbackBlob);
                            });
                        }
                    } catch (e) {
                        console.warn('Logo fallback failed:', e);
                    }
                }

                // Generate PDF with signature
                const signatureDataForPdf = {
                    dataUrl: signatureData.dataUrl,
                    signedBy: signatureData.signedBy,
                    signedAt: new Date()
                };

                const pdfDoc = (
                    <ProposalPdfDocument
                        proposal={proposal}
                        companyLogo={logoBase64}
                        includeInstall={shouldIncludeInstall}
                        selectedSpecies={selectedSpecies}
                        kitSelectedSpecies={kitSelectedSpecies ?? undefined}
                        structureSelections={selectionsSnapshot}
                        generatedDate={new Date()}
                        signature={signatureDataForPdf}
                        showOnlySelected={true}
                        preparedForName={pdfPreparedFor.name}
                        preparedForCompany={pdfPreparedFor.company}
                        projectLabel={headerProjectDescriptor}
                    />
                );
                const pdfBlob = await pdf(pdfDoc).toBlob();

                // Upload to Firebase Storage
                const pdfFileName = `signed-proposals/${proposalId}/${proposal.slug || proposalId}-signed.pdf`;
                const storageRef = ref(storage, pdfFileName);
                await uploadBytes(storageRef, pdfBlob);
                const signedPdfUrl = await getDownloadURL(storageRef);

                // Update proposal with PDF URL. The layout key marks this file as the
                // sign-time structure set so later additions do not replace it.
                const signedPdfLayoutKey = signedIncludedStructureLayoutKey(selectionsSnapshot);
                await updateDoc(doc(db, 'proposalBuilders', proposalId), {
                    signedPdfUrl,
                    ...(signedPdfLayoutKey ? { signedPdfLayoutKey } : {}),
                });

                // Update local state with PDF URL
                setProposal(prev => prev ? { ...prev, signedPdfUrl, signedPdfLayoutKey: signedPdfLayoutKey ?? undefined } as ProposalBuilderData : null);
            } catch (pdfErr) {
                console.error('Error generating signed PDF:', pdfErr);
                // Don't block the signature flow if PDF generation fails
            }

            alert('Thank you for approving this proposal! Our team will be in touch shortly.');
        } catch (err) {
            console.error('Error saving signature:', err);
            const code = (err as { code?: string })?.code;
            const msg = err instanceof Error ? err.message : '';
            if (code === 'permission-denied') {
                alert(
                    'We could not save your signature due to a permissions issue on our end. ' +
                    'Your selections were not recorded. Please contact your project manager — do not retry signing until they confirm the issue is resolved.',
                );
            } else if (msg.includes('undefined') || msg.includes('invalid data')) {
                alert(
                    'We could not save your signature due to a data error on this proposal. ' +
                    'Please contact your project manager so we can resolve it.',
                );
            } else {
                alert('Failed to save signature. Please try again.');
            }
        }
    };

    /**
     * Phased projects: commit an offered later phase. The staff "Offer to client"
     * action pre-created a builder-mode change order carrying this phase's
     * structures; signing here approves that CO so the existing ledger /
     * sales-order / QuickBooks / procurement machinery runs unchanged.
     */
    const handleCommitPhase = async (
        phaseId: string,
        signatureData: { dataUrl: string; signedBy: string },
    ) => {
        if (!proposal || !proposalId) return;
        const phase = getProposalPhaseById(proposal, phaseId);
        if (!phase || phase.status !== 'offered' || !phase.changeOrderId) return;
        const co = dealChangeOrders.find(c => c.id === phase.changeOrderId);
        if (!co || !co.id) {
            setPhaseSignError('This phase is still being prepared. Please try again shortly.');
            return;
        }
        if (co.status === 'approved') return;

        setPhaseSigningId(phaseId);
        setPhaseSignError(null);
        try {
            await ensureAuth();
            const signedAt = Timestamp.now();
            const audit: ChangeOrderAuditEntry = {
                at: new Date(),
                byUid: 'client',
                byName: signatureData.signedBy,
                action: 'signed',
                fromStatus: co.status,
                toStatus: 'approved',
                amount: co.deltaAmount,
            };
            await updateDoc(doc(db, 'changeOrders', co.id), {
                signature: { dataUrl: signatureData.dataUrl, signedBy: signatureData.signedBy, signedAt },
                status: 'approved',
                approvedAt: signedAt,
                auditLog: [...(co.auditLog || []), audit],
                updatedAt: signedAt,
            });

            // Best-effort signed CO PDF (same artifact as the standalone CO page).
            try {
                let logoBase64: string | undefined;
                try {
                    const settings = await getPublicSettings();
                    if (settings.engagementLogo?.url) {
                        const logoUrl = settings.engagementLogo.url.includes('?')
                            ? `${settings.engagementLogo.url}&t=${Date.now()}`
                            : `${settings.engagementLogo.url}?t=${Date.now()}`;
                        const response = await fetch(logoUrl, { mode: 'cors', cache: 'no-store', credentials: 'omit' });
                        if (response.ok) {
                            const blob = await response.blob();
                            logoBase64 = await new Promise<string>((resolve, reject) => {
                                const reader = new FileReader();
                                reader.onloadend = () => resolve(reader.result as string);
                                reader.onerror = () => reject(new Error('FileReader failed'));
                                reader.readAsDataURL(blob);
                            });
                        }
                    }
                } catch { /* logo optional */ }
                const priorContractValue = await computePriorContractValueBeforeChangeOrder(co);
                const signedCo: ChangeOrder = {
                    ...co,
                    signature: { dataUrl: signatureData.dataUrl, signedBy: signatureData.signedBy, signedAt: new Date() },
                    status: 'approved',
                };
                const blob = await pdf(
                    <ChangeOrderPdfDocument changeOrder={signedCo} companyLogo={logoBase64} priorContractValue={priorContractValue} />,
                ).toBlob();
                const path = `signed-change-orders/${co.dealId}/${co.id}-signed.pdf`;
                const storageRef = ref(storage, path);
                await uploadBytes(storageRef, blob);
                const signedPdfUrl = await getDownloadURL(storageRef);
                await updateDoc(doc(db, 'changeOrders', co.id), { signedPdfUrl });
            } catch (pdfErr) {
                console.warn('[PublicProposal] phase CO PDF generation failed', pdfErr);
            }

            // Ledger / sales order / activity + proposal phase stamp (markLinkedProposalPhaseCommitted).
            try {
                await syncApprovedChangeOrder(co.id);
            } catch (syncErr) {
                console.warn('[PublicProposal] phase commit sync failed', syncErr);
            }

            alert(`Thank you! ${phase.name} is now committed. Our team will be in touch shortly.`);
        } catch (err) {
            console.error('[PublicProposal] phase commit failed', err);
            setPhaseSignError('Something went wrong while saving your signature. Please try again.');
        } finally {
            setPhaseSigningId(null);
        }
    };

    // Handle signed PDF download - uses ProposalPdfDocument with signature page
    const handleDownloadSignedPdf = async () => {
        if (!proposal || !proposalId) return;

        const proposalIsSigned = proposal.status === 'approved' || !!proposal.signature;
        if (!proposalIsSigned) {
            alert('This proposal has not been signed yet.');
            return;
        }

        setGeneratingSignedPdf(true);
        const pdfFileName = proposalPdfDownloadName({
            clientName: pdfPreparedFor.name || proposal.clientName,
            projectName: headerProjectDescriptor,
            signed: true,
        });
        const pdfSelectedSpecies: Record<string, string> =
            (typeof proposal.selectedSpecies === 'object' && proposal.selectedSpecies !== null
                ? proposal.selectedSpecies
                : proposal.speciesPricing?.selections)
            || selectedSpecies;

        const logSignedDownload = async (pdfStorageUrl?: string) => {
            if (viewerActorType !== 'client' && viewerActorType !== 'stakeholder') return;
            try {
                const downloadRecord = removeUndefinedDeep({
                    proposalId,
                    dealId: proposal.dealId,
                    clientName: proposal.clientName || 'Unknown',
                    downloadedAt: new Date(),
                    downloadedBy: viewerActorType,
                    includeInstall: effectiveIncludeInstall,
                    selectedSpecies: pdfSelectedSpecies,
                    structureSelections: effectiveStructureSelections,
                    totalAmount: grandTotals.total,
                    pdfStorageUrl: pdfStorageUrl || proposal.signedPdfUrl,
                    pdfFileName,
                    isSigned: true,
                    isKitProposal: proposal.isKitProposal || false,
                    userAgent: navigator.userAgent,
                    ...(kitSelectedSpecies ? { kitSelectedSpecies } : {}),
                });
                const downloadDoc = await addDoc(collection(db, 'proposalDownloads'), downloadRecord);
                await logDownloadToPortalActivity(true, grandTotals.total, downloadDoc.id);
            } catch (logErr) {
                console.warn('Failed to log signed PDF download:', logErr);
            }
        };

        const buildSignedPdfBlob = async (
            signatureData: { dataUrl: string; signedBy: string; signedAt: Date },
        ): Promise<Blob> => {
            const logoBase64 = await loadPdfLogoBase64(companyLogo);

            const pdfDoc = (
                <ProposalPdfDocument
                    proposal={proposal}
                    companyLogo={logoBase64}
                    includeInstall={effectiveIncludeInstall}
                    selectedSpecies={pdfSelectedSpecies}
                    kitSelectedSpecies={kitSelectedSpecies ?? undefined}
                    structureSelections={effectiveStructureSelections}
                    generatedDate={new Date()}
                    signature={signatureData}
                    showOnlySelected={true}
                    preparedForName={pdfPreparedFor.name}
                    preparedForCompany={pdfPreparedFor.company}
                    projectLabel={headerProjectDescriptor}
                />
            );
            return pdf(pdfDoc).toBlob();
        };

        try {
            await ensureAuth();

            const signedPdfIdentityMatches = () => {
                const displayName = (pdfPreparedFor.name || '').trim();
                const displayCompany = (pdfPreparedFor.company || '').trim();
                const storedName = (proposal.clientName || '').trim();
                const storedCompany = (proposal.leadCompany || '').trim();
                return displayName === storedName && displayCompany === storedCompany;
            };

            // A cached signed PDF still has per-structure prices from when every
            // building was included. After an optional structure is dropped those
            // lines are wrong, so regenerate from the current totals.
            const cachedStructurePricesStale = needsDynamicClientPricing(
                proposal,
                effectiveStructureSelections,
            );
            // A save after signing can change install and travel. The stored file was
            // rendered from those later costs, so rebuild it from the signed snapshot.
            const liveContractTotals = computeProposalGrandTotals(proposal, {
                includeInstall: effectiveIncludeInstall,
                selectedSpecies: pdfSelectedSpecies,
                kitSelectedSpecies: kitSelectedSpecies ?? undefined,
                structureSelections: effectiveStructureSelections,
            });
            const signedContractTotals = getSignedGrandTotalsDisplay(proposal);
            const cachedCommercialTotalsStale = !!signedContractTotals
                && liveContractTotals.total !== signedContractTotals.total;
            // Structures added after signing used to be drawn into a rebuilt PDF.
            // A file tagged with the sign-time structure set is kept; an untagged
            // file is rebuilt once from that set and then tagged.
            const cachedLayoutMatchesContract = signedPdfCacheMatchesContractLayout(proposal);

            if (proposal.signedPdfUrl && signedPdfIdentityMatches() && !cachedStructurePricesStale && !cachedCommercialTotalsStale && cachedLayoutMatchesContract) {
                const downloaded = await downloadPdfFromStorageUrl(proposal.signedPdfUrl, pdfFileName);
                if (downloaded) {
                    await logSignedDownload(proposal.signedPdfUrl);
                    return;
                }
                console.warn('[PublicProposal] Cached signed PDF unavailable, regenerating');
            } else if (proposal.signedPdfUrl) {
                console.warn('[PublicProposal] Cached signed PDF is stale, regenerating from the signed totals');
            }

            const resolvedSignature = resolveSignedProposalPdfSignature(proposal);
            if (!resolvedSignature) {
                alert(
                    'The signed PDF cannot be generated because signature data is missing. ' +
                    'Please contact your project manager for a copy.',
                );
                return;
            }

            const pdfBlob = await buildSignedPdfBlob(resolvedSignature);
            downloadBlobAsFile(pdfBlob, pdfFileName);

            try {
                if (!auth.currentUser) {
                    throw new Error('No authenticated session for storage upload');
                }
                const storagePath = `signed-proposals/${proposalId}/${pdfFileName}`;
                const storageRef = ref(storage, storagePath);
                await uploadBytes(storageRef, pdfBlob);
                const signedPdfUrl = await getDownloadURL(storageRef);
                const signedPdfLayoutKey = signedIncludedStructureLayoutKey(
                    getAppliedSignedStructureSnapshot(proposal),
                );
                await updateDoc(doc(db, 'proposalBuilders', proposalId), {
                    signedPdfUrl,
                    ...(signedPdfLayoutKey ? { signedPdfLayoutKey } : {}),
                });
                setProposal(prev => prev ? {
                    ...prev,
                    signedPdfUrl,
                    signedPdfLayoutKey: signedPdfLayoutKey ?? prev.signedPdfLayoutKey,
                } as ProposalBuilderData : null);
                await logSignedDownload(signedPdfUrl);
            } catch (uploadErr) {
                console.warn('Could not upload signed PDF to storage:', uploadErr);
            }
        } catch (err) {
            console.error('Error generating signed PDF:', err);
            alert('Failed to generate signed PDF. Please try again.');
        } finally {
            setGeneratingSignedPdf(false);
        }
    };

    // Handle PDF download
    const handleDownloadPdf = async () => {
        if (!proposal || !proposalId) return;

        setIsGeneratingPdf(true);
        try {
            // Convert logo URL to base64 for PDF (react-pdf has issues with external URLs)
            let logoBase64: string | undefined;
            if (companyLogo?.url) {
                try {
                    // Use cors mode and add cache busting for Firebase Storage URLs
                    const logoUrl = companyLogo.url.includes('?')
                        ? `${companyLogo.url}&t=${Date.now()}`
                        : `${companyLogo.url}?t=${Date.now()}`;
                    const response = await fetch(logoUrl, {
                        mode: 'cors',
                        cache: 'no-cache'
                    });
                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}`);
                    }
                    const blob = await response.blob();
                    logoBase64 = await new Promise<string>((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result as string);
                        reader.onerror = () => reject(new Error('FileReader failed'));
                        reader.readAsDataURL(blob);
                    });
                } catch (e) {
                    console.warn('Failed to load logo from Firebase, trying fallback:', e);
                    // Fallback: try loading from local assets
                    try {
                        const fallbackUrl = '/assets/logo/LC_pdf_logo.png';
                        const fallbackResponse = await fetch(fallbackUrl);
                        if (fallbackResponse.ok) {
                            const fallbackBlob = await fallbackResponse.blob();
                            logoBase64 = await new Promise<string>((resolve, reject) => {
                                const reader = new FileReader();
                                reader.onloadend = () => resolve(reader.result as string);
                                reader.onerror = () => reject(new Error('FileReader failed'));
                                reader.readAsDataURL(fallbackBlob);
                            });
                            console.log('Using fallback logo from local assets');
                        }
                    } catch (fallbackErr) {
                        console.warn('Fallback logo also failed:', fallbackErr);
                    }
                }
            } else {
                // No Firebase logo URL, use local fallback directly
                try {
                    const fallbackUrl = '/assets/logo/LC_pdf_logo.png';
                    const fallbackResponse = await fetch(fallbackUrl);
                    if (fallbackResponse.ok) {
                        const fallbackBlob = await fallbackResponse.blob();
                        logoBase64 = await new Promise<string>((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onloadend = () => resolve(reader.result as string);
                            reader.onerror = () => reject(new Error('FileReader failed'));
                            reader.readAsDataURL(fallbackBlob);
                        });
                        console.log('Using fallback logo (no Firebase logo configured)');
                    }
                } catch (fallbackErr) {
                    console.warn('Failed to load fallback logo:', fallbackErr);
                }
            }

            // Generate PDF blob
            const pdfDoc = (
                <ProposalPdfDocument
                    proposal={proposal}
                    companyLogo={logoBase64}
                    includeInstall={effectiveIncludeInstall}
                    selectedSpecies={selectedSpecies}
                    kitSelectedSpecies={kitSelectedSpecies ?? undefined}
                    structureSelections={effectiveStructureSelections}
                    generatedDate={new Date()}
                    preparedForName={pdfPreparedFor.name}
                    preparedForCompany={pdfPreparedFor.company}
                    projectLabel={headerProjectDescriptor}
                />
            );
            const blob = await pdf(pdfDoc).toBlob();

            // Generate filename and upload to Firebase Storage
            const timestamp = Date.now();
            const pdfClientLabel =
                (pdfPreparedFor.name || '').trim() || proposal.clientName || 'proposal';
            const pdfFileName = proposalPdfDownloadName({
                clientName: pdfClientLabel,
                projectName: headerProjectDescriptor,
            });
            const storagePath = `proposal_downloads/${proposal.dealId || proposalId}/${timestamp}-${pdfFileName}`;
            const storageRef = ref(storage, storagePath);

            let pdfStorageUrl: string | undefined;
            try {
                await uploadBytes(storageRef, blob, { contentType: 'application/pdf' });
                pdfStorageUrl = await getDownloadURL(storageRef);
            } catch (uploadErr) {
                console.error('Error uploading PDF to storage:', uploadErr);
                // Continue with download even if storage fails
            }

            // Create download link for user
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = pdfFileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            // Log download to Firestore with storage URL
            const downloadRecord: Omit<ProposalDownload, 'id'> = {
                proposalId: proposalId,
                dealId: proposal.dealId,
                clientName: pdfClientLabel,
                downloadedAt: new Date(),
                downloadedBy: viewerActorType as 'client' | 'employee' | 'stakeholder',
                includeInstall: effectiveIncludeInstall,
                selectedSpecies: selectedSpecies,
                structureSelections: effectiveStructureSelections,
                totalAmount: grandTotals.total,
                isSigned: false,
                isKitProposal: proposal.isKitProposal || false,
                userAgent: navigator.userAgent,
            };
            // Only add storage URL fields if upload succeeded (Firestore doesn't accept undefined)
            if (pdfStorageUrl) {
                downloadRecord.pdfStorageUrl = pdfStorageUrl;
                downloadRecord.pdfFileName = pdfFileName;
            }
            // Only add kitSelectedSpecies if it has a value (Firestore doesn't accept undefined)
            if (kitSelectedSpecies) {
                downloadRecord.kitSelectedSpecies = kitSelectedSpecies;
            }
            const downloadDoc = await addDoc(collection(db, 'proposalDownloads'), downloadRecord);
            await logDownloadToPortalActivity(false, grandTotals.total, downloadDoc.id);

            // Log download activity notification
            if (proposal.dealId) {
                try {
                    await addDoc(collection(db, 'activities'), {
                        type: 'note',
                        title: 'Proposal Downloaded',
                        description: `${viewerActorName || proposal.clientName || 'Client'} downloaded the proposal PDF for ${formatCurrency(grandTotals.total)}.`,
                        entityType: 'deal',
                        entityId: proposal.dealId,
                        entityName: proposal.clientName,
                        status: 'completed',
                        completedAt: Timestamp.now(),
                        createdBy: 'system',
                        createdAt: Timestamp.now(),
                        updatedAt: Timestamp.now(),
                        actorType: viewerActorType,
                        actorName: viewerActorName || proposal.clientName || 'Client',
                    });
                } catch (actErr) {
                    console.error('Error creating download activity:', actErr);
                }
            }
        } catch (err) {
            console.error('Error generating PDF:', err);
            alert('Failed to generate PDF. Please try again.');
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="proposal-public-page">
                <div className="proposal-loading">
                    <div className="loading-spinner" />
                    <p>Loading proposal...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="proposal-public-page">
                <div className="proposal-error">
                    <h2>Unable to Load</h2>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    if (accessDenied) {
        return (
            <div className="proposal-public-page">
                <div className="proposal-pin-container">
                    <div className="proposal-pin-card">
                        <div className="pin-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                        </div>
                        <h1>Access Restricted</h1>
                        <p>This proposal can only be viewed through your client portal. Please use the link provided to you to access your portal.</p>
                    </div>
                </div>
            </div>
        );
    }

    // Main content
    if (!proposal) return null;

    const isSigned = proposal.status === 'approved' || !!(proposal as any).signature;
    const signedDisplaySignature = resolveSignedProposalPdfSignature(proposal);
    const signingBlocked =
        !isSampleGcDemo &&
        !isStakeholderViewer &&
        !proposalSigningGate.loading &&
        !proposalSigningGate.canSign;
    const conceptApprovalFlowOn = isConceptApprovalFlowEnabled({
        flowDisabled: portalGateContext.gateInput?.conceptApprovalFlowDisabled,
        proposalStatus: proposal.status,
    });
    const returnPortalSlug = portalGateContext.portalSlugForReturn || portalSlug || '';
    const returnPortalProjectId = portalProjectDealId || proposal.dealId || '';

    const structureIncludedOnContract = (structureId: string) => (
        signedLayoutSnapshot
            ? signedLayoutSnapshot[structureId] === true
            : effectiveStructureSelections[structureId] !== false
    );

    const renderStructureOptionalToggle = (ns: ProposalNodeSet, placement: 'gallery' | 'investment') => {
        if (ns.clientOptional !== true) return null;
        const isStructureIncluded = structureIncludedOnContract(ns.id);
        if (isSigned && !isStructureIncluded) return null;

        return (
            <label
                className={`install-toggle${placement === 'gallery' ? ' structure-gallery-toggle' : ''}`}
                onClick={(e) => e.stopPropagation()}
                title={isStructureIncluded ? 'Exclude from proposal' : 'Include in proposal'}
            >
                <input
                    type="checkbox"
                    checked={isStructureIncluded}
                    onChange={(e) => handleStructureToggle(ns.id, e.target.checked)}
                    disabled={isSigned}
                    aria-label={`${isStructureIncluded ? 'Exclude' : 'Include'} ${ns.name}`}
                />
                <span className="toggle-slider" />
            </label>
        );
    };

    const renderOptionalStructuresPanel = () => {
        if (optionalStructures.length === 0) return null;

        return (
            <section className="optional-structures-section" aria-label="Optional structures">
                <h3 className="section-heading">Optional Add-Ons</h3>
                <p className="optional-structures-hint">
                    {isSigned
                        ? 'These selections were saved when you signed the proposal.'
                        : 'Turn add-ons off if you don\u2019t need them — your total updates instantly.'}
                </p>
                {!isSigned && (
                    <p className="optional-structures-batching-note">
                        Pricing reflects the efficiency of building these structures as one project —
                        removing add-ons will adjust the remaining structure totals.
                    </p>
                )}
                <ul className="optional-structures-list">
                    {optionalStructures.map(ns => {
                        const isStructureIncluded = structureIncludedOnContract(ns.id);
                        return (
                            <li
                                key={ns.id}
                                className={`optional-structure-row${!isStructureIncluded ? ' optional-structure-row--excluded' : ''}`}
                            >
                                {renderStructureOptionalToggle(ns, 'investment')}
                                <span className="optional-structure-name">{ns.name}</span>
                                {(ns.quantity || 1) > 1 && (
                                    <span className="optional-structure-qty">×{ns.quantity}</span>
                                )}
                                {!isStructureIncluded ? (
                                    <span className="optional-structure-excluded">Excluded</span>
                                ) : (
                                    <span className="optional-structure-included">Included</span>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </section>
        );
    };

    const renderExclusiveGroupsPanel = () => {
        if (exclusiveStructures.length < 2) return null;

        return (
            <section className="optional-structures-section exclusive-structures-section" aria-label="Choose one structure">
                <div className="exclusive-structure-group">
                    <h3 className="section-heading">Choose one</h3>
                    <p className="optional-structures-hint">
                        {isSigned
                            ? 'This selection was saved when you signed the proposal.'
                            : 'Only one of these structures can be purchased.'}
                    </p>
                    <ul className="optional-structures-list" role="radiogroup" aria-label="Choose one">
                        {exclusiveStructures.map(ns => {
                            const isStructureIncluded = structureIncludedOnContract(ns.id);
                            if (isSigned && !isStructureIncluded) return null;
                            return (
                                <li
                                    key={ns.id}
                                    className={`optional-structure-row${!isStructureIncluded ? ' optional-structure-row--excluded' : ''}`}
                                >
                                    <label className="exclusive-structure-radio">
                                        <input
                                            type="radio"
                                            name="exclusive-structures"
                                            checked={isStructureIncluded}
                                            disabled={isSigned}
                                            onChange={() => handleStructureToggle(ns.id, true)}
                                            aria-label={`Select ${ns.name}`}
                                        />
                                        <span className="exclusive-structure-radio-mark" aria-hidden />
                                    </label>
                                    <span className="optional-structure-name">{ns.name}</span>
                                    {(ns.quantity || 1) > 1 && (
                                        <span className="optional-structure-qty">×{ns.quantity}</span>
                                    )}
                                    {isStructureIncluded ? (
                                        <span className="optional-structure-included">Selected</span>
                                    ) : (
                                        <span className="optional-structure-excluded">Not selected</span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </section>
        );
    };

    /**
     * Post-signing commit blocks for offered phases. Phase totals now live in
     * the investment total block; this section only appears when a later phase
     * is ready for the client to commit (keeps the portal `#phase-{id}` anchor
     * and signature flow).
     */
    const renderPhasesSection = () => {
        if (!isPhasedProposal(proposal) || !isSigned) return null;
        const readyGroups = groupNodeSetsByPhase(proposal)
            .filter(({ phase }) => phase.status === 'offered');
        if (readyGroups.length === 0) return null;

        return (
            <section className="proposal-phases-section" aria-label="Phases ready to commit">
                <h3 className="section-heading">Ready to Commit</h3>
                <p className="proposal-phases-hint">
                    Your team has released the following phase{readyGroups.length > 1 ? 's' : ''} for
                    commitment. Signing adds the phase to your existing contract.
                </p>
                <div className="proposal-phases-list">
                    {readyGroups.map(({ phase, nodeSets: phaseNodeSets }) => {
                        const phaseCo = phase.changeOrderId
                            ? dealChangeOrders.find(c => c.id === phase.changeOrderId)
                            : undefined;
                        const amount = phaseCo ? phaseCo.deltaAmount : computePhaseCommitAmount(proposal, phase.id);

                        return (
                            <div
                                key={phase.id}
                                id={`phase-${phase.id}`}
                                className="proposal-phase-card phase-offered phase-ready"
                            >
                                <div className="proposal-phase-card-header">
                                    <div className="proposal-phase-card-title">
                                        <span className="proposal-phase-card-name">{phase.name}</span>
                                    </div>
                                    <span className="proposal-phase-card-status phase-offered">
                                        Ready to commit
                                    </span>
                                </div>
                                <div className="proposal-phase-card-body">
                                    <span className="proposal-phase-card-structures">
                                        {phaseNodeSets.length > 0
                                            ? phaseNodeSets.map(ns => ns.name).join(', ')
                                            : 'Scope to be defined'}
                                    </span>
                                    <span className="proposal-phase-card-amount">{formatCurrency(amount)}</span>
                                </div>
                                <div className="proposal-phase-commit">
                                    {phaseCo ? (
                                        <>
                                            <p className="proposal-phase-commit-copy">
                                                Your team has released <strong>{phase.name}</strong> for commitment
                                                at <strong>{formatCurrency(amount)}</strong>. Signing below adds this
                                                phase to your contract ({getChangeOrderReference(phaseCo)}).
                                            </p>
                                            <div className="proposal-phase-commit-schedule">
                                                <span>50% deposit {formatCurrency(Math.round(amount * 0.5))}</span>
                                                <span>40% at production completion {formatCurrency(Math.round(amount * 0.4))}</span>
                                                <span>10% at delivery/install {formatCurrency(Math.round(amount * 0.1))}</span>
                                            </div>
                                            {phaseSignError && (
                                                <p className="proposal-phase-commit-error">{phaseSignError}</p>
                                            )}
                                            {isSampleGcDemo ? (
                                                <SignaturePad
                                                    onSign={() => undefined}
                                                    demoOnly
                                                />
                                            ) : isStakeholderViewer ? (
                                                <p className="proposal-phase-commit-copy">
                                                    View only — committing is available to the client.
                                                </p>
                                            ) : (
                                                <SignaturePad
                                                    onSign={(sig) => handleCommitPhase(phase.id, sig)}
                                                    disabled={phaseSigningId === phase.id}
                                                />
                                            )}
                                        </>
                                    ) : (
                                        <p className="proposal-phase-commit-copy">
                                            This phase is being prepared for commitment — your team will let you
                                            know when it&apos;s ready to sign.
                                        </p>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>
        );
    };

    return (
        <div className="proposal-public-page">
            {/* Background */}
            {companyBackground && (
                <div
                    className="proposal-background"
                    style={{ backgroundImage: `url(${companyBackground.url})` }}
                />
            )}
            <div className="proposal-background-overlay" />

            <div className="proposal-container">
                {/* Back to Portal */}
                {cameFromPortal && portalSlug !== 'preview' && (
                    <button
                        className="proposal-back-btn"
                        onClick={async () => {
                            const backTok =
                                stakeholderTokenParam
                                || (portalSlug ? sessionStorage.getItem(`portal_stakeholder_token_${portalSlug}`) : null);
                            const q = new URLSearchParams();
                            if (backTok) q.set('stakeholderToken', backTok);
                            if (portalProjectDealId) q.set('project', portalProjectDealId);
                            if (portalSlug && portalSlug !== 'preview') {
                                try {
                                    const accessMeta = await getPortalAccessMeta(portalSlug);
                                    const activitySource =
                                        viewerActorType === 'employee'
                                            ? 'employee'
                                            : viewerActorType === 'stakeholder'
                                              ? 'stakeholder'
                                              : 'client';
                                    await logPortalActivity(
                                        accessMeta.portalId,
                                        portalProjectDealId || proposal?.dealId || null,
                                        'link_click',
                                        'returned_to_portal',
                                        {
                                            proposalSlug: slug || proposal?.slug || '',
                                            projectName:
                                                (displayProjectNameFromPortal || '').trim()
                                                || portalTabProjectNameFallback
                                                || undefined,
                                        },
                                        activitySource,
                                    );
                                } catch {
                                    /* non-blocking */
                                }
                            }
                            const qs = q.toString();
                            navigate(`/portal/${portalSlug}${qs ? `?${qs}` : ''}`);
                        }}
                    >
                        <ArrowLeft size={16} />
                        Back to Portal
                    </button>
                )}

                {isSampleGcDemo ? (
                    <div className="proposal-demo-banner" role="status">
                        <strong>Sample proposal</strong>
                        {' — '}Demo only.
                    </div>
                ) : isStakeholderViewer && stakeholderDisplayName ? (
                    <div
                        className="proposal-stakeholder-banner"
                        role="status"
                    >
                        <strong>Welcome, {stakeholderDisplayName}</strong>
                        {' — '}You&apos;re viewing as a <strong>stakeholder</strong> (view only). This proposal is prepared for{' '}
                        <strong>{proposal.clientName || 'the client'}</strong>. You cannot sign the contract on their behalf.
                    </div>
                ) : null}

                {/* Combined Header - Logo and Thank You */}
                <header className="proposal-header-module">
                    {/* Logo */}
                    <div className="header-logo">
                        {companyLogo ? (
                            <img src={companyLogo.url} alt="Leiper's Creek Timber Frames" className="proposal-logo" />
                        ) : (
                            <h1 className="proposal-company-name">Leiper's Creek Timber Frames</h1>
                        )}
                    </div>

                    {/* Divider Line */}
                    <div className="header-divider" />

                    {/* Thank You Message */}
                    <div className="header-message">
                        <p className="thank-you-message">
                            Thank you for considering <strong>Leiper's Creek Timber Frames</strong> for your project.
                            We're honored by the opportunity to craft something truly exceptional for you.
                        </p>
                        <p className="owner-signature">
                            — Matt Lynch, Owner
                        </p>
                    </div>
                </header>

                {/* Video Section */}
                {videoUrl && (
                    <section className="video-section">
                        <h3 className="section-heading">Learn About Our Process</h3>
                        <div className="video-embed-container">
                            <div className="video-embed-wrapper">
                                <iframe
                                    src={videoUrl.includes('/embed/') ? videoUrl : videoUrl.replace('watch?v=', 'embed/')}
                                    title="Learn About Our Timber Framing Process"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            </div>
                        </div>
                    </section>
                )}

                {/* Client Info Module */}
                <section className="client-info-section">
                    <div className="client-info-card">
                        <p className="prepared-for-label">Prepared For</p>
                        {headerLeadCompany && (
                            <p className="client-company">{headerLeadCompany}</p>
                        )}
                        <h2 className="client-name">{headerClientName}</h2>
                        {!proposal.isKitProposal && (headerProjectNumber || headerProjectDescriptor) && (
                            <p className="project-name">
                                {headerProjectNumber && (
                                    <span className="project-number">{headerProjectNumber}</span>
                                )}
                                {headerProjectDescriptor && (
                                    <span className="project-descriptor">{headerProjectDescriptor}</span>
                                )}
                            </p>
                        )}
                        {proposal.location && (
                            <p className="client-location">{proposal.location}</p>
                        )}
                    </div>
                </section>

                {/* Kit Title Module - Only for kit proposals */}
                {proposal.isKitProposal && proposal.sourceKitName && (
                    <section className="kit-title-section">
                        <div className="kit-title-card">
                            <p className="kit-label">Timber Kit</p>
                            <h2 className="kit-name">{proposal.sourceKitName}</h2>
                        </div>
                    </section>
                )}

                {renderOptionalStructuresPanel()}
                {renderExclusiveGroupsPanel()}

                {/* Structures Gallery Section
                    Visible when ANY enabled structure has images, when timber sizes
                    are enabled, OR when optional structures need client toggles. */}
                {showStructuresGallery && (
                    <section className="structures-gallery-section">
                        <h3 className="section-heading">Your Timber Frame Structures</h3>
                        <div className="structures-gallery">
                            {sortedGalleryNodeSets.map(ns => {
                                const timberRowsForStructure = timberSummariesByStructureId[ns.id] || [];
                                const isStructureIncluded = structureIncludedOnContract(ns.id);
                                const isOptionalStructure = ns.clientOptional === true;
                                const isExclusiveStructure = exclusiveStructures.length > 1 && structureIsExclusive(ns);
                                const canShowTimberLink = showTimberSizes && timberRowsForStructure.length > 0 && isStructureIncluded;
                                return (
                                <div
                                    key={ns.id}
                                    className={`structure-gallery-card ${(isOptionalStructure || isExclusiveStructure) && !isStructureIncluded ? 'structure-gallery-excluded' : ''}`}
                                >
                                    {ns.images && ns.images.length > 0 ? (
                                        <div
                                            className="structure-gallery-images clickable"
                                            onClick={() => openLightbox(ns.images || [], 0)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => e.key === 'Enter' && openLightbox(ns.images || [], 0)}
                                        >
                                            <img
                                                src={ns.images[0].url}
                                                alt={ns.name}
                                                className="structure-gallery-image"
                                            />
                                            {ns.images.length > 1 && (
                                                <div className="structure-image-count">
                                                    +{ns.images.length - 1}
                                                </div>
                                            )}
                                            <div className="image-zoom-hint">
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="11" cy="11" r="8" />
                                                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                                    <line x1="11" y1="8" x2="11" y2="14" />
                                                    <line x1="8" y1="11" x2="14" y2="11" />
                                                </svg>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="structure-gallery-placeholder">
                                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9z" />
                                                <polyline points="9 22 9 12 15 12 15 22" />
                                            </svg>
                                        </div>
                                    )}
                                    <div className="structure-gallery-info">
                                        <div className="structure-gallery-info-top">
                                            <span className="structure-gallery-name">{ns.name}</span>
                                            {isOptionalStructure && (
                                                <span className="optional-badge">Optional</span>
                                            )}
                                            {isExclusiveStructure && (
                                                <span className="optional-badge exclusive-badge" title="Only one of these structures can be purchased">
                                                    Choose 1
                                                </span>
                                            )}
                                            {isPhasedProposal(proposal) && (() => {
                                                const ph = getProposalPhaseById(proposal, getNodeSetPhaseId(proposal, ns));
                                                return ph ? (
                                                    <span className={`structure-gallery-phase-badge phase-${ph.status}`}>
                                                        {ph.name}
                                                    </span>
                                                ) : null;
                                            })()}
                                            {(ns.quantity || 1) > 1 && (
                                                <span className="structure-gallery-qty">×{ns.quantity}</span>
                                            )}
                                        </div>
                                        {(isOptionalStructure || isExclusiveStructure) && !isStructureIncluded && (
                                            <span className="structure-gallery-excluded-label">Excluded from total</span>
                                        )}
                                        {canShowTimberLink && (
                                            <button
                                                type="button"
                                                className="structure-gallery-timber-link"
                                                onClick={() => setOpenTimberFor(ns.id)}
                                                aria-haspopup="dialog"
                                                aria-label={`View timber sizes for ${ns.name}`}
                                            >
                                                <span>View timber sizes</span>
                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                                    <polyline points="9 18 15 12 9 6" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    </section>
                )}

                {/* Investment Breakdown - Expandable Buckets */}
                <section className="investment-section">
                    <h3 className="section-heading">Your Investment</h3>

                    {proposal.isRomPricing && (
                        <div className="rom-pricing-notice">
                            <strong>ROM Pricing</strong>
                            <p>This is Rough Order of Magnitude (ROM) pricing. The figures below are preliminary and will be refined after we receive your input and finalize project details.</p>
                        </div>
                    )}

                    {/* Design & Engineering Bucket */}
                    <div className={`investment-bucket ${expandedBuckets['design'] ? 'expanded' : ''}`}>
                        <div className="bucket-header" onClick={() => clientVisibleNodeSets.length > 1 && !proposal.isKitProposal && !proposal.designIncludedInKit && toggleBucket('design')}>
                            <div className="bucket-info">
                                <div className="bucket-icon design">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M12 19l7-7 3 3-7 7-3-3z" />
                                        <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
                                    </svg>
                                </div>
                                <span className="bucket-name">Design & Engineering</span>
                            </div>
                            <div className="bucket-total">
                                {proposal.isKitProposal || proposal.designIncludedInKit ? (
                                    <span className="included-badge">Included</span>
                                ) : (
                                    <>
                                        {formatCurrency(grandTotals.design)}
                                        {clientVisibleNodeSets.length > 1 && (
                                            expandedBuckets['design'] ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                        {expandedBuckets['design'] && clientVisibleNodeSets.length > 1 && (
                            <div className="bucket-breakdown">
                                {breakdownGroups.map(group => (
                                    <React.Fragment key={group.phase?.id || 'all'}>
                                        {group.phase && (
                                            <div className="breakdown-phase-header">{group.phase.name}</div>
                                        )}
                                        {group.nodeSets.map(ns => (
                                            <div key={ns.id} className="breakdown-item">
                                                <span>
                                                    {ns.name}
                                                    {(ns.quantity || 1) > 1 && <span className="qty-badge">×{ns.quantity}</span>}
                                                </span>
                                                <span>{formatCurrency(calculateProposalNodeBucketsWithSelections(ns, proposal.profitMargin, proposal, effectiveStructureSelections).design)}</span>
                                            </div>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Timber Kit Bucket */}
                    <div className={`investment-bucket ${timberKitDetailsOpen ? 'expanded' : ''}`}>
                        <div
                            className="bucket-header"
                            onClick={() => {
                                const isExpandable = proposal.isKitProposal
                                    ? kitTimberBucketExpandable
                                    : clientVisibleNodeSets.length > 1 || hasMultipleSpecies;
                                if (isExpandable) toggleBucket('timberKit');
                            }}
                        >
                            <div className="bucket-info">
                                <div className="bucket-icon timber">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M4 6h16M4 12h16M4 18h16" />
                                        <path d="M4 6v12" />
                                        <path d="M20 6v12" />
                                    </svg>
                                </div>
                                <div className="bucket-name-group">
                                    <span className="bucket-name">Timber Kit</span>
                                    <span className="bucket-subtitle">
                                        {(proposal.isKitProposal || proposal.designIncludedInKit) ? 'Design, Materials, Production & Freight' : 'Materials, Production & Freight'}
                                    </span>
                                </div>
                            </div>
                            <div className="bucket-total">
                                {formatCurrency(grandTotals.timberKit)}
                                {(() => {
                                    const isExpandable = proposal.isKitProposal
                                        ? kitTimberBucketExpandable
                                        : clientVisibleNodeSets.length > 1 || hasMultipleSpecies;
                                    return isExpandable && (
                                        expandedBuckets['timberKit'] ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                                    );
                                })()}
                            </div>
                        </div>

                        {/* Kit proposal species selector - per structure for multi-structure kits */}
                        {showKitSpeciesBreakdown && (
                            <div className="bucket-breakdown">
                                {clientVisibleNodeSets.length > 1 ? (
                                    // Multi-structure kit: show per-structure species dropdown
                                    clientVisibleNodeSets.map((ns) => {
                                        const nsIndex = proposal.nodeSets.findIndex(n => n.id === ns.id);
                                        const currentSelection = selectedSpecies[ns.id] || kitSelectedSpecies || proposal.activeSpecies || proposal.offeredSpecies?.[0] || '';

                                        // Calculate price for this structure with selected species
                                        // Use kitPricing.structurePricing (same as grandTotals) for consistency
                                        const speciesPricing = proposal.kitPricing?.find(kp => kp.speciesName === currentSelection);
                                        let structurePrice: number;
                                        if (clientVisibleNodeSets.length === 1) {
                                            structurePrice = grandTotals.timberKit;
                                        } else if (speciesPricing?.structurePricing && speciesPricing.structurePricing[nsIndex]) {
                                            // Use kit's per-structure pricing by index (nodeSet IDs may differ from kit)
                                            structurePrice = speciesPricing.structurePricing[nsIndex].cost || 0;
                                        } else if (ns.billablePrices?.speciesOptions?.[currentSelection] !== undefined) {
                                            // Fallback to pre-calculated species-specific pricing
                                            structurePrice = ns.billablePrices.speciesOptions[currentSelection];
                                        } else if (ns.billablePrices?.timberKit !== undefined) {
                                            // Fallback to default timber kit price
                                            structurePrice = ns.billablePrices.timberKit;
                                        } else {
                                            // Last resort: divide total equally
                                            structurePrice = (speciesPricing?.totalCost || 0) / clientVisibleNodeSets.length;
                                        }

                                        return (
                                            <div key={ns.id} className="breakdown-item with-species">
                                                <div className="breakdown-structure">
                                                    <span className="structure-name">
                                                        {ns.name}
                                                        {(ns.quantity || 1) > 1 && <span className="qty-badge">×{ns.quantity}</span>}
                                                    </span>
                                                    {kitOfferedSpeciesCount <= 1 ? (
                                                        <div className="species-display-static" role="status">
                                                            {formatClientTimberSpecLabel(timberListForSpeciesSelection(ns.materials, currentSelection), currentSelection)}
                                                        </div>
                                                    ) : (
                                                        <select
                                                            className="species-dropdown"
                                                            value={currentSelection}
                                                            onChange={(e) => handleSpeciesChange(ns.id, e.target.value)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            disabled={isSigned}
                                                        >
                                                            {proposal.offeredSpecies?.map((species) => (
                                                                <option key={species} value={species}>
                                                                    {formatClientTimberSpecLabel(timberListForSpeciesSelection(ns.materials, species), species)}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    )}
                                                    <StructureDeckingNote materials={ns.materials} />
                                                </div>
                                                <span className="breakdown-price">{formatCurrency(structurePrice)}</span>
                                            </div>
                                        );
                                    })
                                ) : (
                                    // Single structure kit: show global species dropdown
                                    <div className="kit-species-inside-dropdown">
                                        <div className="breakdown-structure">
                                            <span className="structure-name">{proposal.sourceKitName || 'Timber Kit'}</span>
                                            {kitOfferedSpeciesCount <= 1 ? (
                                                <div className="species-display-static" role="status">
                                                    {formatClientTimberSpecLabel(
                                                        clientVisibleNodeSets[0]
                                                            ? timberListForSpeciesSelection(clientVisibleNodeSets[0].materials, kitSelectedSpecies || proposal.activeSpecies || proposal.offeredSpecies?.[0] || '')
                                                            : [],
                                                        kitSelectedSpecies || proposal.activeSpecies || proposal.offeredSpecies?.[0] || ''
                                                    )}
                                                </div>
                                            ) : (
                                                <select
                                                    className="species-dropdown"
                                                    value={kitSelectedSpecies || proposal.activeSpecies || proposal.offeredSpecies?.[0] || ''}
                                                    onChange={(e) => handleKitSpeciesChange(e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    disabled={isSigned}
                                                >
                                                    {proposal.offeredSpecies?.map((species) => (
                                                        <option key={species} value={species}>
                                                            {formatClientTimberSpecLabel(
                                                                clientVisibleNodeSets[0]
                                                                    ? timberListForSpeciesSelection(clientVisibleNodeSets[0].materials, species)
                                                                    : [],
                                                                species,
                                                            )}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                            {clientVisibleNodeSets[0] && (
                                                <StructureDeckingNote materials={clientVisibleNodeSets[0].materials} />
                                            )}
                                        </div>
                                        <span className="breakdown-price">{formatCurrency(grandTotals.timberKit)}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Custom proposal per-structure breakdown */}
                        {showCustomTimberBreakdown && (
                            <div className="bucket-breakdown">
                                {breakdownGroups.map(group => (
                                <React.Fragment key={group.phase?.id || 'all'}>
                                {group.phase && (
                                    <div className="breakdown-phase-header">{group.phase.name}</div>
                                )}
                                {group.nodeSets.map(ns => {
                                    const hasAlternates = ns.materials.quoteMultipleSpecies &&
                                        (ns.materials.alternateSpecies?.length ?? 0) > 0;
                                    const currentSelection = selectedSpecies[ns.id] || 'default';
                                    const defaultSpecies = ns.materials.timberList[0]?.species || 'Default';

                                    const timberKitPrice = clientVisibleNodeSets.length === 1
                                        ? grandTotals.timberKit
                                        : clientStructureTimberKitForDisplay(
                                            proposal,
                                            ns,
                                            selectedSpecies,
                                            effectiveStructureSelections,
                                        );

                                    return (
                                        <div key={ns.id} className="breakdown-item with-species">
                                            <div className="breakdown-structure">
                                                <span className="structure-name">
                                                    {ns.name}
                                                    {(ns.quantity || 1) > 1 && <span className="qty-badge">×{ns.quantity}</span>}
                                                </span>
                                                {hasAlternates ? (
                                                    <select
                                                        className="species-dropdown"
                                                        value={currentSelection}
                                                        onChange={(e) => handleSpeciesChange(ns.id, e.target.value)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        disabled={isSigned}
                                                        aria-label={`Timber species for ${ns.name}`}
                                                    >
                                                        <option value="default">{formatClientTimberSpecLabel(ns.materials.timberList, defaultSpecies)}</option>
                                                        {ns.materials.alternateSpecies?.map(alt => (
                                                            <option key={alt.speciesName} value={alt.speciesName}>
                                                                {formatClientTimberSpecLabel(alt.timberList, alt.speciesName)}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <div className="species-display-static" role="status" aria-label={`Timber species for ${ns.name}`}>
                                                        {formatClientTimberSpecLabel(ns.materials.timberList, defaultSpecies)}
                                                    </div>
                                                )}
                                                {ns.notes && (
                                                    <span className="structure-notes">{ns.notes}</span>
                                                )}
                                                <StructureDeckingNote materials={ns.materials} />
                                            </div>
                                            <span className="breakdown-price">{formatCurrency(timberKitPrice)}</span>
                                        </div>
                                    );
                                })}
                                </React.Fragment>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Installation Bucket */}
                    <div className={`investment-bucket ${expandedBuckets['install'] ? 'expanded' : ''} ${!effectiveIncludeInstall && proposal.installOptional ? 'excluded' : ''}`}>
                        <div className="bucket-header" onClick={() => clientVisibleNodeSets.length > 1 && toggleBucket('install')}>
                            <div className="bucket-info">
                                {proposal.installOptional && !(isSigned && !effectiveIncludeInstall) && (
                                    <label className="install-toggle" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={effectiveIncludeInstall}
                                            onChange={(e) => handleInstallToggle(e.target.checked)}
                                            disabled={isSigned}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                )}
                                <div className="bucket-icon install">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                                    </svg>
                                </div>
                                <div className="bucket-name-group">
                                    <span className="bucket-name">
                                        Installation
                                        {proposal.installOptional && !(isSigned && !effectiveIncludeInstall) && (
                                            <span className="optional-badge">Optional</span>
                                        )}
                                    </span>
                                    <span className="bucket-subtitle">
                                        Labor, Equipment{hasTravel ? ' & Travel' : ''}
                                    </span>
                                </div>
                            </div>
                            <div className="bucket-total">
                                {effectiveIncludeInstall
                                    ? (grandTotals.install === 0
                                        ? <span className="install-not-included-text">Not Included</span>
                                        : formatCurrency(grandTotals.install))
                                    : <span className="excluded-text">Excluded</span>}
                                {clientVisibleNodeSets.length > 1 && (
                                    expandedBuckets['install'] ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                                )}
                            </div>
                        </div>
                        {expandedBuckets['install'] && clientVisibleNodeSets.length > 1 && effectiveIncludeInstall && (
                            <div className="bucket-breakdown">
                                {breakdownGroups.map(group => (
                                <React.Fragment key={group.phase?.id || 'all'}>
                                {group.phase && (
                                    <div className="breakdown-phase-header">{group.phase.name}</div>
                                )}
                                {group.nodeSets.map(ns => {
                                    const installCost = calculateProposalNodeBucketsWithSelections(
                                        ns,
                                        proposal.profitMargin,
                                        proposal,
                                        effectiveStructureSelections,
                                    ).install;
                                    return (
                                        <div key={ns.id} className="breakdown-item">
                                            <span>
                                                {ns.name}
                                                {(ns.quantity || 1) > 1 && <span className="qty-badge">×{ns.quantity}</span>}
                                            </span>
                                            <span>
                                                {installCost === 0
                                                    ? <span className="install-not-included-text">Not Included</span>
                                                    : formatCurrency(installCost)}
                                            </span>
                                        </div>
                                    );
                                })}
                                </React.Fragment>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Bonding — surety premium pass-through, follows the selections above */}
                    {grandTotals.bonding > 0 && (
                        <div className="investment-bucket investment-bucket-bonding">
                            <div className="bucket-header" style={{ cursor: 'default' }}>
                                <div className="bucket-info">
                                    <div className="bucket-icon bonding">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
                                            <path d="m9 12 2 2 4-4" />
                                        </svg>
                                    </div>
                                    <div className="bucket-name-group">
                                        <span className="bucket-name">{bondingLineLabel(proposal.globalCosts?.bonding)}</span>
                                        <span className="bucket-subtitle">{bondingLineDescription(proposal.globalCosts?.bonding)}</span>
                                    </div>
                                </div>
                                <div className="bucket-total">{formatCurrency(grandTotals.bonding)}</div>
                            </div>
                        </div>
                    )}

                    {/* Promotional discount — labeled line + description above the total */}
                    {discountLine && (
                        <div className="investment-discount">
                            <div className="investment-discount-row">
                                <span className="investment-discount-label">{discountLine.label}</span>
                                <span className="investment-discount-amount">−{formatCurrency(discountLine.amount)}</span>
                            </div>
                            {discountLine.description && (
                                <p className="investment-discount-desc">{discountLine.description}</p>
                            )}
                        </div>
                    )}

                    {/* Grand Total — phased proposals list per-phase totals, then the signing sum */}
                    {proposalIsPhased && signingPhaseRows.length > 0 ? (
                        <div className="investment-total-block">
                            {signingPhaseRows.map(({ phase, amount }) => (
                                <div key={phase.id} className="investment-total-phase-row">
                                    <span>{phase.name}</span>
                                    <span>{formatCurrency(amount)}</span>
                                </div>
                            ))}
                            <div className="investment-total">
                                <span>{isSigned ? 'Signed Total' : 'Total In This Signing'}</span>
                                <span className="total-amount-wrap">
                                    {publicCostPerSqFt != null && (
                                        <span
                                            className="total-psf-badge"
                                            tabIndex={0}
                                            data-tip={PROJECT_COST_PER_SQFT_HOVER}
                                            aria-label={`${publicCostPerSqFt} dollars per square foot. ${PROJECT_COST_PER_SQFT_HOVER}`}
                                        >
                                            ${publicCostPerSqFt}/sf
                                        </span>
                                    )}
                                    <span className="total-amount">{formatCurrency(grandTotals.total)}</span>
                                </span>
                            </div>
                            {hasPhasesOutsideSigning && (
                                <p className="investment-phase-note">
                                    Future phases are excluded from this total and will be offered separately.
                                </p>
                            )}
                        </div>
                    ) : (
                        <div className="investment-total">
                            <span>Total Investment</span>
                            <span className="total-amount-wrap">
                                {publicCostPerSqFt != null && (
                                    <span
                                        className="total-psf-badge"
                                        tabIndex={0}
                                        data-tip={PROJECT_COST_PER_SQFT_HOVER}
                                        aria-label={`${publicCostPerSqFt} dollars per square foot. ${PROJECT_COST_PER_SQFT_HOVER}`}
                                    >
                                        ${publicCostPerSqFt}/sf
                                    </span>
                                )}
                                <span className="total-amount">{formatCurrency(grandTotals.total)}</span>
                            </span>
                        </div>
                    )}
                </section>

                {renderPhasesSection()}

                {/* Payment Schedule Section */}
                <section className="payment-schedule-section">
                    <h3 className="section-heading payment-schedule-heading">
                        <span>
                            {isPhasedProposal(proposal) ? 'Payment Schedule (per phase commitment)' : 'Payment Schedule'}
                        </span>
                        {isSigned && (
                            <PortalStatementButton
                                projectName={headerProjectDescriptor || headerProjectName || headerClientName || 'Project'}
                                clientCompany={headerLeadCompany}
                                clientName={headerClientName}
                                projectNumber={headerProjectNumber || null}
                                originalContract={paymentScheduleBreakdown.baseTotal}
                                changeOrderTotal={paymentScheduleBreakdown.currentContractValue - paymentScheduleBreakdown.baseTotal}
                                currentContractValue={paymentScheduleBreakdown.currentContractValue}
                                changeOrders={dealChangeOrders}
                                schedule={paymentScheduleBreakdown.rows.map((row) => ({
                                    id: row.id,
                                    label: row.label,
                                    detail: row.detail,
                                    amount: row.amount,
                                    kind: row.kind,
                                    baseAmount: row.baseAmount,
                                    folded: row.coAdjustments.map((adj) => ({
                                        reference: adj.reference,
                                        title: adj.title,
                                        amount: adj.amount,
                                    })),
                                }))}
                                statuses={paymentInstallmentStatuses}
                                activity={portalStatement}
                            />
                        )}
                    </h3>
                    {!isSigned && (
                        <p className="payment-schedule-note">
                            {proposal.isRomPricing
                                ? 'Estimated from this ROM total — not a contract payment schedule.'
                                : 'Estimated from the current proposal total — final amounts are set at signing.'}
                        </p>
                    )}
                    <PaymentScheduleBreakdown
                        breakdown={paymentScheduleBreakdown}
                        variant="dark"
                        statuses={paymentInstallmentStatuses}
                    />
                </section>

                {/* Signature Section */}
                <section className="signature-section" id="proposal-signature">
                    {proposal.isRomPricing && !isSigned ? (
                        <>
                            <h3 className="section-heading">ROM Pricing Only</h3>
                            <div className="rom-pricing-notice rom-pricing-notice--signature">
                                <strong>ROM Pricing</strong>
                                <p>
                                    This is Rough Order of Magnitude (ROM) pricing. These figures are
                                    preliminary and cannot be signed or approved. Final pricing will be
                                    published when this proposal is ready for approval.
                                </p>
                            </div>
                        </>
                    ) : (
                    <>
                    <h3 className="section-heading">Approval & Signature</h3>
                    {isSampleGcDemo ? (
                        <div className="signature-pad-wrapper">
                            <SignaturePad onSign={() => undefined} demoOnly />
                        </div>
                    ) : isSigned ? (
                        <div className="already-signed">
                            <div className="signed-badge">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                    <polyline points="22 4 12 14.01 9 11.01" />
                                </svg>
                                <span>Proposal Approved</span>
                            </div>
                            {signedDisplaySignature && (
                                <div className="signature-details">
                                    <img src={signedDisplaySignature.dataUrl} alt="Signature" className="signature-image" />
                                    <p>Signed by: {signedDisplaySignature.signedBy}</p>
                                    <p>Date: {signedDisplaySignature.signedAt.toLocaleDateString()}</p>
                                </div>
                            )}
                            <button
                                className="download-signed-btn"
                                onClick={handleDownloadSignedPdf}
                                disabled={generatingSignedPdf}
                            >
                                {generatingSignedPdf ? (
                                    <>
                                        <Loader2 size={18} className="spinning" />
                                        Generating PDF...
                                    </>
                                ) : (
                                    <>
                                        <FileCheck size={18} />
                                        Download Signed Proposal
                                    </>
                                )}
                            </button>
                        </div>
                    ) : signingBlocked ? (
                        <div className="signature-gated">
                            <p className="signature-instructions">
                                {conceptApprovalFlowOn
                                    ? proposalSigningGate.reasons.includes('no_deliverables')
                                        ? 'Signing opens after we release your concept model. You can still review this proposal and download a PDF.'
                                        : 'Signing opens after you approve the concept model in your portal. You can still review this proposal and download a PDF.'
                                    : 'This proposal is not open for signature yet. You can still review it and download a PDF.'}
                            </p>
                            {conceptApprovalFlowOn &&
                            returnPortalSlug &&
                            shouldRedirectProposalSignToConceptModel(proposalSigningGate.reasons) ? (
                                <button
                                    type="button"
                                    className="signature-gated-return"
                                    onClick={() =>
                                        navigate(
                                            portalConceptModelReturnPath(
                                                returnPortalSlug,
                                                returnPortalProjectId,
                                            ),
                                        )
                                    }
                                >
                                    Review concept model
                                </button>
                            ) : null}
                        </div>
                    ) : proposalSigningGate.loading ? (
                        <div className="signature-pad-wrapper">
                            <p className="signature-instructions" style={{ marginBottom: 0 }}>
                                Checking deliverable approvals…
                            </p>
                        </div>
                    ) : isStakeholderViewer ? (
                        <div className="signature-pad-wrapper">
                            <p className="signature-instructions" style={{ marginBottom: 0 }}>
                                View only — signing is available to the client through their portal link.
                            </p>
                        </div>
                    ) : (
                        <div className="signature-pad-wrapper">
                            <p className="signature-instructions">
                                By signing below, you approve this proposal and agree to the terms and conditions outlined below.
                            </p>
                            <SignaturePad onSign={handleSign} />
                        </div>
                    )}
                    </>
                    )}
                </section>

                {/* Testimonials Section - Before T&C, Collapsed by default */}
                <CollapsibleSection title="What Our Clients Say" defaultOpen={false}>
                    <div className="testimonials-grid">
                        <blockquote className="testimonial">
                            <p>"If I could give ten stars, I would. Everyone who sees my new workshop is blown away by the attention to detail and craftsmanship. Matt shepherded this project throughout the entire process."</p>
                            <cite>— Robert McCrary, Franklin, TN</cite>
                        </blockquote>
                        <blockquote className="testimonial">
                            <p>"We had an excellent experience with Leiper's Creek Timber Frames. The craftsmanship was top-notch and the attention to detail throughout the project was outstanding."</p>
                            <cite>— Joe Crider, Legend Homes, Franklin, TN</cite>
                        </blockquote>
                        <blockquote className="testimonial">
                            <p>"Absolutely 1st Class experience: On Time! On budget! Professional Craftsmanship! Amazing Process from design, engineering and construction. The team was professional, courteous, clean and efficient at their craft! The end product for me far exceeded my expectations!"</p>
                            <cite>— Hal Lowe, Franklin, TN</cite>
                        </blockquote>
                    </div>
                </CollapsibleSection>

                {/* Terms & Conditions Section - Below Signature, Collapsed by default */}
                <CollapsibleSection title="Terms & Conditions" defaultOpen={false}>
                    <div className="terms-content">
                        {getProposalTermsConditions(headerClientName || '', proposal.globalCosts?.bonding).split('\n\n').map((para: string, i: number) => (
                            <p key={i}>{para}</p>
                        ))}
                    </div>
                </CollapsibleSection>



                {/* Unsigned proposal PDF — hide after sign (signed PDF is offered above). */}
                {!isSigned && (
                <section className="download-section">
                    <button
                        className="download-proposal-btn"
                        onClick={handleDownloadPdf}
                        disabled={isGeneratingPdf}
                    >
                        {isGeneratingPdf ? (
                            <>
                                <span className="spinner"></span>
                                Generating PDF...
                            </>
                        ) : (
                            <>
                                <Download size={20} />
                                Download this Proposal
                            </>
                        )}
                    </button>
                </section>
                )}

                {/* Footer */}
                <footer className="proposal-footer">
                    <p>This proposal is valid for 30 days from the date of issue.</p>
                    <p className="proposal-contact">
                        Questions? Contact us at <a href="mailto:info@lctimberframes.com">info@lctimberframes.com</a>
                    </p>
                    <p className="copyright">© {new Date().getFullYear()} Leiper's Creek Timber Frames</p>
                </footer>
            </div>

            {/* Lightbox Modal */}
            {lightboxOpen && lightboxImages.length > 0 && (
                <div className="lightbox-overlay" onClick={closeLightbox}>
                    <button className="lightbox-close" onClick={closeLightbox}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>

                    {lightboxImages.length > 1 && (
                        <>
                            <button
                                className="lightbox-nav lightbox-prev"
                                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                            >
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="15 18 9 12 15 6" />
                                </svg>
                            </button>
                            <button
                                className="lightbox-nav lightbox-next"
                                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                            >
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="9 18 15 12 9 6" />
                                </svg>
                            </button>
                        </>
                    )}

                    <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                        <img
                            src={lightboxImages[lightboxIndex].url}
                            alt={lightboxImages[lightboxIndex].name || `Image ${lightboxIndex + 1}`}
                            className="lightbox-image"
                        />
                        {lightboxImages.length > 1 && (
                            <div className="lightbox-counter">
                                {lightboxIndex + 1} / {lightboxImages.length}
                            </div>
                        )}
                    </div>

                    {lightboxImages.length > 1 && (
                        <div className="lightbox-thumbnails" onClick={(e) => e.stopPropagation()}>
                            {lightboxImages.map((img, idx) => (
                                <button
                                    key={idx}
                                    className={`lightbox-thumb ${idx === lightboxIndex ? 'active' : ''}`}
                                    onClick={() => setLightboxIndex(idx)}
                                >
                                    <img src={img.url} alt={img.name || `Thumbnail ${idx + 1}`} />
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Timber Sizes sheet — opt-in via proposal.publicSettings.showTimberSizes.
                Mounted only while a structure is selected so closing fully tears down. */}
            {openTimberFor && (() => {
                const ns = proposal.nodeSets.find(n => n.id === openTimberFor);
                if (!ns) return null;
                if (signedLayoutSnapshot && !(ns.id in signedLayoutSnapshot)) return null;
                return (
                    <TimberSizesSheet
                        open
                        onClose={() => setOpenTimberFor(null)}
                        structureName={ns.name}
                        rows={timberSummariesByStructureId[ns.id] || []}
                    />
                );
            })()}
        </div>
    );
};

export default PublicProposalPage;
